import {useFrame, useThree} from "@react-three/fiber";
import {observable} from "mobx";
import {useEffect, useRef} from "react";
import * as THREE from "three";
import {Assert} from "js-vextensions";
import {RunInAction_Set} from "web-vcore";
import {GetMapObjects} from "../../Server/DB/mapObjects.js";
import {GetTerrainMaterialsMods, ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../Server/DB/mapObjects/@MapObject.js";
import {Mod_TerrainDefinition, PoissonSolverConfig, TerrainHeightColorGradient, TerrainHeightShadingConfig} from "../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_TerrainDefinition.js";
import {GetProcMatRecipes, ResolveRecipe} from "../../Server/DB/mapObjects/ProcMatAccessors.js";
import {DMap} from "../../Server/DB/maps/@Map.js";
import {MapState} from "../../Store/main/maps/@MapState.js";
import {PathMaterialFeature} from "../../Terrain/Materials/@MaterialFeature.js";
import {ambientDetailUniformValues} from "../../Terrain/Materials/Compose/AmbientDetailStage.js";
import {computeLightDirJS, sampleHypsometricRampJS} from "../../Terrain/Materials/Compose/LightingStage.js";
import {packRegionParamsWithFeather} from "../../Terrain/Materials/Compose/RegionParams.js";
import {REGION_PARAMS_COLUMNS} from "../../Terrain/Materials/Compose/ShaderAssembler.js";
import type {ResolvedRecipe} from "../../Terrain/Materials/RecipeResolverCore.js";
import type {MaterialRegion} from "../../Terrain/Materials/Splat/MaterialRegions.js";
import {collectMaterialRegions} from "../../Terrain/Materials/Splat/MaterialRegions.js";
import {assignRegionSlots, enumerateChunkRecipeSets, filterRegionsContributingToChunk, rasterizeSplatFromArrays, SplatRasterizeError} from "../../Terrain/Materials/Splat/SplatRasterizer.js";
import {SPLAT_MAX_MATERIALS} from "../../Terrain/Materials/Splat/SplatTypes.js";
import {buildAmbientWeightMaskTexture, buildGeoHeightTexture, buildGeoNormalTexture, buildRegionTextures, buildSplatTextures, TerrainMaterialManager} from "../../Terrain/Materials/TerrainMaterialManager.js";
import {mapZIndexes} from "../../UI/Maps/MapUI/MapSurface/MapZIndexes.js";
import {MobXTreeToPlainTree} from "../../Utils/General/MobX.js";
import {observer_mgl_r3} from "../../Utils/ThreeJS/General.js";
import {Invalidate3JS} from "../../Utils/ThreeJS/Invalidate3JS.js";
import {Rect} from "../../Utils/ThreeJS/VectorStructs.js";
import {collectRasterFeatures} from "./Poisson/@Shared/ConstraintRasterizer.js";
import {ResolveMapObjectsForTerrain} from "./Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain_Resolver.js";
import {RasterFeature, rasterFeaturesSignature} from "./Poisson/@Shared/ConstraintRasterizer/RasterFeatures_Pure.js";
import {
    snapshotActivations,
    sumActivationSnapshots,
    type BackendComponentActivations,
} from "./Poisson/BackendCompActivations.js";
import {terrainDebugUpdate} from "./TerrainDebug.js";
import {terrainDebug3DUpdate} from "./TerrainDebug3D.js";
import {terrainProcessStats} from "./TerrainProcessStats.js";
import {CHUNK_N, QuadtreeNode, TerrainQuadtree, TerrainRegionRect} from "./TerrainQuadtree.js";
import {buildTerrainGeo3D} from "./TerrainGeometry3D.js";
import {resolveMode3DFineCellSize, resolveMode3DWorkingGridRadius, resolveRenderChunkMaxSamplesPerFineCellEdge, resolveViewInfo, resolveFinestLWavelength} from "./TerrainViewInfo.js";
import {resolveActiveViewpoint} from "./ViewpointCamera.js";
import {terrainShadowLight_current, terrainShadowMapSize_current} from "./TerrainShadows.js";
import {createTerrainDepthMaterial, setTerrainDepthUniforms} from "../Materials/TerrainShadowDepthMaterial.js";
import type {PreparedTerrainDataV3} from "./TerrainWorker.js";
import {MapObject_ForTerrain} from "./Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain.js";

const ROOT_HALF = 65536; // root quad covers ±65536 world units

/** A square polygon (closed path shape) centred at (cx, cy) with half-extent `r` — the demo
 *  regions' geometry (world-space, pre-tessellated — the packaged path shape). */
function squarePoly(cx: number, cy: number, r: number): {x: number; y: number}[] {
	return [
		{x: cx - r, y: cy - r}, {x: cx + r, y: cy - r},
		{x: cx + r, y: cy + r}, {x: cx - r, y: cy + r},
	];
}

// Cap in-flight batches per worker: prevents worker 0 (first to ACK receiverReady) from monopolising
// the eval queue while workers 1..N are still importing the shared solved grid. Surplus nodes stay
// queued in the quadtree until primeEvalAcrossAllWorkers distributes them once all workers are ready.
const MAX_INFLIGHT_PER_WORKER = 5; // high enough to work through most temporary main-thread lockups

// ── Set12 P2 — default material REGIONS (the demo fallback; plan §6) ─────────────
// The Set9 elevation-band `assignments` were deleted in P1; the demo is re-expressed as
// `PathMaterialFeature` regions in the NEW shape: a large dirt fill + a high-elevation
// cobblestone fill.  Used only when the map defines no `materialFeature` paths of its own.
const SPLAT_DEMO_REGIONS: MaterialRegion[] = [
	{
		id: "demo_dirt",
		pathFeature: new PathMaterialFeature({recipeID: "dirt", mode: "fill", feather: 8}),
		polylines: [squarePoly(0, 0, 65536)],
		closed: true,
		dominance: 0,
		bbox: new Rect(-65536, -65536, 131072, 131072, false),
	},
	{
		id: "demo_cobble_high",
		pathFeature: new PathMaterialFeature({recipeID: "cobblestone", mode: "fill", feather: 20}),
		polylines: [squarePoly(0, 0, 32768)],
		closed: true,
		dominance: 1,
		bbox: new Rect(-32768, -32768, 65536, 65536, false),
	},
];

/** The active material REGIONS (the P2 rasterizer input). Module-level mutable (devtools console
 *  can swap the demo set).  Set12: driven by `ObjPath.materialFeature` → `collectMaterialRegions`
 *  from P2 onward; the demo regions above render when the map has none of its own. */
export let terrainMaterialRegions: MaterialRegion[] = SPLAT_DEMO_REGIONS;
export function setTerrainMaterialRegions(regions: MaterialRegion[]): void {
	terrainMaterialRegions = regions;
}

/** The map's base (fallback) recipe — slot 0 of every chunk's splat.  Reads the first enabled
 *  `Mod_TerrainMaterials.baseRecipeID` (default "dirt" when none). */
export function terrainBaseRecipeID(mapID: string): string {
	return GetTerrainMaterialsMods(mapID)[0]?.baseRecipeID ?? "dirt";
}

/** The P2 per-chunk rasterizer options: the active regions + the map's base recipe. */
function splatOptsFor(mapID: string) {
	return {regions: terrainMaterialRegions, baseRecipeID: terrainBaseRecipeID(mapID)};
}

/** P4 — the chunk world sizes the warm-up enumerator sweeps over (the quadtree's LOD levels).
 *  The quadtree subdivides the root (ROOT_HALF*2 = 131072 m) by powers of 2 down to the finest
 *  chunk at max zoom (16 m as of 2026-08-26 — the old 1024 m floor predated deep-zoom leaves,
 *  and chunks SMALLER than the sweep never got their per-set program + variant row warmed:
 *  a sub-1024 m chunk's bbox-overlap region set is not a subset of the coarse sizes' sets, so
 *  its identity set was unknown to getProgramForSet/getVariantForSet → variant row 0 → BLACK
 *  chunks at deep zoom, the set changing as the LOD sizes change). */
// Set13 — re-enabled: the warm-up is now `compileAsync`-backed (non-blocking; three uses
// KHR_parallel_shader_compile when available and falls back to a poll otherwise), so the 
// "warm up locks up the page" problem that forced this to be set to 16_999_999_999 is gone.
// Restored to its intended value (the finest chunk size, 16 m — see the Set12 comment below).
const WARM_UP_MIN_CHUNK_SIZE = 16_999_999;
function chunkSizesForWarmUp(): number[] {
	const sizes: number[] = [];
	for (let s = ROOT_HALF * 2; s >= WARM_UP_MIN_CHUNK_SIZE; s /= 2) sizes.push(s);
	return sizes;
}

/** P4 — a chunk's EXPECTED recipe set (slot order, slot 0 = the base) from region bbox ↔ chunk
 *  overlap — the SAME bbox-overlap + narrow-phase + slot-assignment logic the rasteriser's broad
 *  phase uses ({@link filterRegionsContributingToChunk} + {@link assignRegionSlots}), so the
 *  per-chunk program fetch (getProgramForSet) hits the warmed program.  The rasterised
 *  `chunk.recipeIDs` is authoritative; a mismatch (a broad-phase edge case) swaps the program after rasterise. */
function chunkRecipeSetFor(
	x: number, y: number, w: number, h: number,
	regions: readonly MaterialRegion[],
	opts: {knownRecipeIDs: string[]; baseRecipeID: string; maxMaterials: number},
): string[] {
	const filtered = filterRegionsContributingToChunk(regions, {x, y, width: w, height: h});
	return assignRegionSlots(filtered.regions, {knownRecipeIDs: new Set(opts.knownRecipeIDs), baseRecipeID: opts.baseRecipeID, maxMaterials: opts.maxMaterials}).recipeIDs;
}

/** A cheap signature of the ACTIVE region set + the map's base recipe — the splat field the
 *  rasteriser produces is a pure function of this (the base slot-0 recipe + the region
 *  recipes/geometry/dominance/param modulations + blend fields).  Compared across renders: a
 *  change means every chunk's splat (index/distance/region/params textures) is stale and must be
 *  re-rasterised + the live program re-ensured (the region-signature block below).  JSON of the
 *  regions' bridge-visible fields is the strict form; the set is tiny (a handful of regions).
 *
 *  **2026-08-13 — `baseRecipeID` is folded in.**  Changing the map's base recipe swaps slot 0 of
 *  EVERY chunk's material set — a STRUCTURAL change (the slot-0 recipe's GLSL eval is part of the
 *  program).  It must therefore invalidate the same way a region edit does (full rebuild:
 *  dispose chunk clones, clear splats, re-ensure the program).  Without it here, a base change
 *  fell through to the param-OVERLAP signature block below, which treated it as a params-only
 *  edit: `refreshParamValues` re-packed the OLD base's values into the stale program's UBO (no
 *  recompile), while the re-rasterised splats carried the NEW base in slot 0 — the fallback areas
 *  kept the old base and the shader's slot→recipe mapping misread every other slot (the "another
 *  material got replaced" symptom).  Folding it in routes a base change to the correct rebuild. */
function materialRegionsSignature(regions: readonly MaterialRegion[], baseRecipeID: string): string {
	return JSON.stringify({
		base: baseRecipeID,
		regions: regions.map(r=>({
			id: r.id,
			dominance: r.dominance,
			closed: r.closed,
			polylines: r.polylines,
			recipeID: r.pathFeature.recipeID,
			mode: r.pathFeature.mode,
			strokeWidth: r.pathFeature.strokeWidth ?? null,
			feather: r.pathFeature.feather ?? null,
			featherCurve: r.pathFeature.featherCurve ?? null,
			opaque: r.pathFeature.opaque ?? null,
			edgeNoise: r.pathFeature.edgeNoise ?? null,
			paramModulations: r.pathFeature.paramModulations ?? null,
			// P6 — masks are part of the rasteriser's per-sample input (they bake into the stored
			// distances), so a mask edit must invalidate every chunk's splat (re-rasterise).
			masks: r.pathFeature.masks ?? null,
		})),
	});
}

/** Set12 P3 — build a chunk's region textures (plan §6 + §3.6): the per-chunk region index
 *  texture (winning region per sample — the region-params ROW) + the PER-MAP params texture (one
 *  row per region; columns = the fixed FEATHER block + the regionModulated params' slots).  The
 *  params texture is rebuilt per chunk (cheap — a few floats per region; the chunk-scoped
 *  texture is simplest and avoids a separate cache), from each region's blend fields +
 *  `pathFeature.paramModulations` keyed by the recipe's param keys. */
function buildRegionTexturesForMap(
	regions: Uint8Array,
	materialRegions: readonly MaterialRegion[],
	mapID: string,
): {regionTex: THREE.DataTexture; paramsTex: THREE.DataTexture} | null {
	// Resolve the active recipe set (slot order — same as the splat's recipeIDs).
	const recipeIDs = splatRecipeIDs(terrainBaseRecipeID(mapID));
	const resolvedRecipes = recipeIDs.map(id=>ResolveRecipe(id, mapID));
	const columns = REGION_PARAMS_COLUMNS;
	// Region rows: one per REGION in the FULL map list, PLUS a dummy row 0 for the base — the P2
	// rasterizer's per-sample region payload is the map-region index PLUS ONE (0 = base, so the
	// first map region's index 0 never collides with "no region"); the shader's region-params
	// fetch is texelFetch(u_regionParams, ivec2(column, g_regionIndex)) with g_regionIndex = the
	// payload — row 0 (the base) holds the resolved defaults (never read meaningfully; a sample
	// with no winning region doesn't fetch region params).
	const rows = Math.max(1, materialRegions.length) + 1;
	const regionValues = [{}, ...materialRegions.map(r=>r.pathFeature.paramModulations ?? {})];
	const paramsFloat = packRegionParamsWithFeather(resolvedRecipes, rows, columns, regionValues, materialRegions);
	return buildRegionTextures(regions, paramsFloat, rows, columns);
}

/** The distinct recipe ids the active splat assignments reference, in assignment order — slot 0
 *  is ALWAYS the map's BASE (fallback) recipe, followed by the region recipes.  Drives
 *  ensureProgram's recipe set — the ≤4-way shader evaluates exactly these.  Unknown ids are
 *  caught by the rasterizer.  Set12 P2: the ids come from the REGION set (the module-level
 *  `terrainMaterialRegions` + the map's base recipe).  **2026-08-12 — the base is now prepended
 *  (slot 0)** so the fast-path program's slot layout ALWAYS matches the rasterizer's base-first
 *  splat (a base-less program would misread every rasterized splat's slots — the
 *  GrassInsideSnow snow→water bug's fallback path).  The rasterizer's own `recipeIDs` is built
 *  by `assignRegionSlots` with the same base-first convention, so the fast-path program and the
 *  per-chunk splats agree.
 *  @param baseRecipeID The map's fallback recipe (slot 0).  Defaults to "dirt" when not given —
 *  callers that know the map pass `terrainBaseRecipeID(map.id)` so a custom base is honoured. */
export function splatRecipeIDs(baseRecipeID = "dirt"): string[] {
	// CANONICAL slot order — the SAME ranking {@link assignRegionSlots} uses (dominance DESC,
	// recipe-id ASC), so the fast-path program's eval order ALWAYS matches the rasterized
	// splat's slot order for every chunk.  (A region-list-order here would compile the fast-path
	// program with e.g. `[dirt, water, grass, snow]` while the rasterizer writes
	// `[dirt, grass, water, snow]` — both reduce to the same ORDER-INSENSITIVE set hash, so
	// whichever compiled first wins, and slot 1's distance [grass] gets read as water's eval →
	// lake/ocean renders as grass.  The 2026-08-12 canonical-slot fix covered the per-chunk
	// rasterizer but not this fast-path builder; the GrassInsideSnow test only pinned the
	// rasterizer's order.)
	const knownRecipeIDs = new Set([baseRecipeID, ...terrainMaterialRegions.map(r=>r.pathFeature.recipeID).filter(id=>id != "")]);
	const {recipeIDs} = assignRegionSlots(terrainMaterialRegions, {
		knownRecipeIDs,
		baseRecipeID,
		maxMaterials: SPLAT_MAX_MATERIALS,
	});
	// Cap to the splat's material limit (the ≤4-way shader).  The rasterizer enforces the same
	// cap per chunk (base + ≤3 regions); the fast-path program must never exceed it or
	// `ensureProgram`'s `resolved.length > 4` guard would throw.
	return recipeIDs.slice(0, SPLAT_MAX_MATERIALS);
}

/** 2026-08-08 — a cheap signature of the ACTIVE recipes' param VALUES (per assignment recipe id →
 *  the RESOLVED defaults, which carry the recipes' `paramOverrides`).  A params-only recipe edit
 *  changes this WITHOUT changing the assignment signature (see the `prevParamOverridesSignatureRef`
 *  block).  The signature is a map of JSON — it must stay cheap (a handful of recipes × ~10
 *  params, re-resolved per render through the mobx accessor's cache — the accessors re-notify the
 *  render body on a DB change, which is what makes this block re-run on a recipe edit). */
function paramOverridesSignature(mapID: string): string {
	const ids = splatRecipeIDs(terrainBaseRecipeID(mapID));
	const byID = new Map<string, string>();
	for (const id of ids) {
		let resolved: ResolvedRecipe | null = null;
		try { resolved = ResolveRecipe(id, mapID); } catch { /* unknown/erroring id → signature "!" (unresolvable) */ }
		if (resolved) byID.set(id, JSON.stringify(resolved.params.map(p=>({k: p.key, d: p.default}))));
		else byID.set(id, "!");
	}
	return JSON.stringify([...byID.entries()]);
}

/** The active terrain-quadtree instance, set when the renderer mounts. Null when terrain is disabled. */
export let terrainQuadtree_current: TerrainQuadtree|null = null;

/** The active TerrainWorkerPool instance, set when the renderer mounts. Null when terrain is disabled/unmounted.
 *  Held at module scope so the devtools-console {@link terrain_collectBackendCompActivations} hook can reach
 *  the live pool without a React ref handoff. */
export let terrainWorkerPool_current: TerrainWorkerPool|null = null;

/** The latest set of PreparedTerrainDataV3 entries, keyed by mod hash. Updated whenever prepareTerrain completes. */
export let terrainPreparedData_current: Map<string, PreparedTerrainDataV3> = new Map();
/** The current ordered list of V3 RasterFeature entries used for the last prepareTerrain dispatch
 *  (the §7.1 two-grid cache reads these at solve time — the renderer forwards them as plain objects
 *  so the worker can rasterise + re-solve on a feature-set change). */
export let terrainFeatures_current: RasterFeature[] = [];
/** Incremented each time terrainPreparedData_current is updated; observe this to react to new data. */
export const terrainDataVersion = observable.box(0);

/** Per-chunk splat info committed by the renderer's frame loop (see `splatMapRef`): the ACTUAL
 *  recipe ids the chunk rasterized (`chunkRecipeIDs`, slot 0 = the map's base), not a re-derivation
 *  from the region set.  Published at module scope so the Stats panel reads ground truth. */
export interface TerrainChunkSplatInfo {
	chunkRecipeIDs?: string[];
	texVersion: number;
}
/** The live per-chunk splat map, published by the renderer on mount (a reference to the same Map it
 *  mutates each frame, so it always reflects the CURRENTLY committed chunk data). Null when terrain
 *  is disabled/unmounted. */
export let terrainChunkSplats_current: ReadonlyMap<QuadtreeNode, TerrainChunkSplatInfo> | null = null;

/** The committed chunk's recipe ids + its grid X/Y index (a leaf's column/row in the root-quad grid,
 *  i.e. `(bounds.x + ROOT_HALF) / bounds.width`).  Null when no committed chunk splat covers the point. */
export function terrainCommittedChunkInfoAtPoint(wx: number, wy: number): {recipeIDs: string[]; indexX: number; indexY: number} | null {
	const splats = terrainChunkSplats_current;
	const qt = terrainQuadtree_current;
	if (splats == null || qt == null) return null;
	const leaf = qt.findDeepestLeafAt(wx, wy);
	if (leaf == null) return null;
	const entry = splats.get(leaf);
	if (entry == null || entry.chunkRecipeIDs == null) return null;
	const W = leaf.bounds.width;
	return {
		recipeIDs: entry.chunkRecipeIDs,
		indexX: Math.round((leaf.bounds.x + ROOT_HALF) / W),
		indexY: Math.round((leaf.bounds.y + ROOT_HALF) / W),
	};
}

// ---------------------------------------------------------------------------
// Devtools-console hook — collect + sum the per-worker activation counters.
// ---------------------------------------------------------------------------

declare global {
	interface Window {
		/** Collect the per-backend-per-component activation counters from every terrain
		 *	worker (and the renderer thread itself) into one summed {@link BackendComponentActivations}.
		 *	Re-issues the broadcast each call — the result is a fresh point-in-time read, not a cache. */
		terrain_collectBackendCompActivations?: ()=>Promise<BackendComponentActivations>;
	}
}

/** Sums per-worker + renderer-thread activation snapshots into one {@link BackendComponentActivations}.
 *  Returns all-zeroes when no pool is live. Freshly allocated each call — safe to hold.
 *  Devtools: `await window.terrain_collectBackendCompActivations()` */
export async function collectBackendCompActivations(): Promise<BackendComponentActivations> {
	const pool = terrainWorkerPool_current;
	const workerSnaps = pool ? await pool.collectBackendCompActivations() : [];
	// Always include the renderer-thread singleton — the main page may host CPU solves too
	// (e.g. a future in-page solver path, or the Node vitest lane where there are no workers
	// but the singleton is the whole truth).
	const own = snapshotActivations();
	return sumActivationSnapshots([...workerSnaps, own]);
}

// Install devtools hook at module load; re-installed on hot-reload so it always points
// to the latest closure + `terrainWorkerPool_current`.
if (typeof window !== "undefined") {
	window.terrain_collectBackendCompActivations = collectBackendCompActivations;
}

// ---------------------------------------------------------------------------
// Worker pool
// ---------------------------------------------------------------------------

type WorkerCallback = (data: Record<string, unknown>)=>void;

// 2026-07-30 receiver-readiness ACK — fired when a receiver posts `receiverReady` after importing a SharedSolvedGrid.
// The renderer gates evalChunkBatch on per-worker readiness to avoid the receiverWaiting/zero-fill race.
type ReceiverReadyCallback = (workerIdx: number, cacheKey: string, cameraGen: number)=>void;

class TerrainWorkerPool {
	private workers: Worker[];
	private next = 0;
	private pending = new Map<number, WorkerCallback>();
	private idCounter = 1;
	// 2026-07-30 receiver-readiness ACK — maps each Worker to its pool index so the shared onmessage
	// can route id-less `receiverReady` ACKs back to the per-worker readiness tracker.
	private workerForOnmessage = new WeakMap<Worker, number>();
	private onReceiverReady: ReceiverReadyCallback | null = null;  // set by the renderer
	// 2026-07-30 cross-tab pollution guard — per-pool token namespacing the worker-side solve-share
	// BroadcastChannel so a stale same-origin tab can't deliver stale-schema snapshots. See ctor.
	private readonly _channelToken: string;

	constructor(workerCount: number) {
		// 2026-07-30 cross-tab pollution guard — one token per pool; see the `_channelToken` docblock.
		this._channelToken = Math.random().toString(36).slice(2, 10);
		this.workers = Array.from({length: workerCount}, (_unused, i)=>{
			const w = new Worker(new URL("./TerrainWorker.js", import.meta.url));
			// `Array.from`'s second arg IS the worker index; `this.workers` is still undefined
			// here (the assignment completes only after Array.from returns), so read the index
			// from the callback arg instead of `this.workers.length`.
			this.workerForOnmessage.set(w, i);
			w.onmessage = (e: MessageEvent)=>{
				// 2026-07-30 receiver-readiness ACK — surface `receiverReady` (no `id`) BEFORE the id-keyed reply path.
				if (e.data?.type === "receiverReady") {
					const idx = this.workerForOnmessage.get(w) ?? -1;
					if (idx >= 0 && this.onReceiverReady) {
						this.onReceiverReady(idx, e.data.cacheKey as string, e.data.cameraGen as number);
					}
					return;
				}
				// 2026-07-29 solve-share: worker 0 publishes solved-grid snapshots on a worker-side
				// BroadcastChannel directly to receivers (1..N) — the renderer is no longer in the
				// fan-out path.  This handler only resolves id-keyed worker replies.
				const cb = this.pending.get(e.data.id);
				if (cb) { this.pending.delete(e.data.id); cb(e.data); }
			};
			return w;
		});
		// Ship token to all workers so they switch BroadcastChannel to the namespaced name
		// before worker 0's first publish. Overrides the legacy static name from eager module load.
		this.dispatchFireAndForgetBroadcast({type: "initChannelToken", channelToken: this._channelToken});
	}

	get workerCount() { return this.workers.length; }

	/** 2026-07-30 cross-tab pollution guard — the per-pool BroadcastChannel namespacing token. */
	get channelToken(): string { return this._channelToken; }

	/** 2026-07-30 receiver-readiness ACK — register the renderer's `receiverReady` listener. */
	setReceiverReadyListener(cb: ReceiverReadyCallback): void { this.onReceiverReady = cb; }


	/** Round-robin worker index selector — advances the internal `next` counter and returns
	 *  the next worker index modulo the pool size.  Public so the renderer's demand-pull
	 *  eval-dispatch helper can pick a worker without reaching into the private `next` field. */
	roundRobinWorkerIdx(): number { return this.next++ % this.workers.length; }

	/** Round-robin dispatch for cache-agnostic tasks (buildTexture has no per-mod cache). */
	dispatch(msg: Record<string, unknown>, transfer: Transferable[], onDone: WorkerCallback) {
		this.dispatchToWorker(this.next++ % this.workers.length, msg, transfer, onDone);
	}

	/** Dispatch to a specific worker — required for `prepareTerrain`+`evalChunk` so the
	 *	worker's `workerCaches.get(cacheKey)` hit is guaranteed; a miss fills zeros → black tile. */
	dispatchToWorker(workerIdx: number, msg: Record<string, unknown>, transfer: Transferable[], onDone: WorkerCallback) {
		const id = this.idCounter++;
		this.pending.set(id, onDone);
		msg.id = id;
		this.workers[workerIdx % this.workers.length].postMessage(msg, transfer);
	}

	/** Broadcast to all workers; fires `onDone` once every worker has replied. */
	dispatchBroadcast(msg: Record<string, unknown>, transferPerWorker: Transferable[][], onDone: (responses: Record<string, unknown>[])=>void) {
		const n = this.workers.length;
		const responses: Record<string, unknown>[] = Array(n);
		let remaining = n;
		// Capture the (per-worker) message id mapping: each worker gets its own id; we map
		// them back to the worker index via a side table so the responses array is ordered.
		const workerForId = new Map<number, number>();
		for (let i = 0; i < n; i++) {
			const id = this.idCounter++;
			workerForId.set(id, i);
			this.pending.set(id, data=>{
				responses[i] = data;
				if (--remaining === 0) onDone(responses);
			});
			// `msg.id` is intentionally overwritten per-worker — each worker receives a fresh id.
			msg.id = id;
			const transfer = transferPerWorker[i] ?? [];
			this.workers[i].postMessage(msg, transfer);
		}
	}

	/** Fire-and-forget broadcast — no pending-callback registered, so workers that never reply
	 *  don't leak `pending` entries. Each worker gets a shallow-cloned msg with a fresh id
	 *  (for handler symmetry; unused reply-side). No transfer-list support needed. */
	dispatchFireAndForgetBroadcast(msg: Record<string, unknown>) {
		const n = this.workers.length;
		for (let i = 0; i < n; i++) {
			// Fresh id per worker (handler symmetry; no `pending.set`).  Shallow clone per
			// worker so the `msg.id` assignment doesn't alias across workers (worker reads
			// `msg.id` only for reply-routing, which we don't do here, but the isolate works).
			const perWorker = {...msg, id: this.idCounter++};
			this.workers[i].postMessage(perWorker, []);
		}
	}

	/** Promise-based broadcast for per-worker activation snapshots (ordered by worker index).
	 *  Stale `pending` entries remain if a worker never replies; `undefined` holes are tolerated by the sum helper.
	 *  Resolves with an empty array if the pool has no live workers. */
	collectBackendCompActivations(): Promise<BackendComponentActivations[]> {
		const n = this.workers.length;
		if (n === 0) return Promise.resolve([]);
		const responses: (BackendComponentActivations | undefined)[] = Array(n).fill(undefined);
		const workerForId = new Map<number, number>();
		return new Promise(resolveAll=>{
			for (let i = 0; i < n; i++) {
				const id = this.idCounter++;
				workerForId.set(id, i);
				this.pending.set(id, data=>{
					responses[i] = (data?.snapshot as BackendComponentActivations | undefined) ?? undefined;
					if (responses.every(r=>r != null)) {
						resolveAll(responses as BackendComponentActivations[]);
					}
				});
				this.workers[i].postMessage({type: "getBackendCompActivations", id});
			}
		});
	}

	terminate() {
		for (const w of this.workers) w.terminate();
		this.workers = [];
		this.pending.clear();
	}
}

function bitmapToTexture(bitmap: ImageBitmap): THREE.Texture {
	const tex = new THREE.Texture(bitmap);
	tex.magFilter = THREE.LinearFilter;
	tex.minFilter = THREE.LinearFilter;
	tex.colorSpace = THREE.SRGBColorSpace;
	// ImageBitmap ignores UNPACK_FLIP_Y_WEBGL in many browsers, so we write pixels
	// in natural bottom-up GL order in the worker and disable the GPU flip here.
	tex.flipY = false;
	tex.needsUpdate = true;
	return tex;
}

// Each visible leaf gets one mesh. We pool & reuse them to avoid GC churn.
class ChunkMeshPool {
	private pool: THREE.Mesh[] = [];
	private scene: THREE.Scene;
	/** 2D unit quad (PlaneGeometry 1×1), shared by every 2D chunk (the per-chunk transform is
	 *  position+scale on the mesh — the geometry itself is never mutated). */
	private geo2D: THREE.PlaneGeometry;
	/** Set15 P3 (§3.7) — the shared 3D unit-space subdivided+skirt BufferGeometry: a 33×33
	 *  surface grid plus a duplicated perimeter ring flagged `aSkirt` (see
	 *  {@link buildTerrainGeo3D} in TerrainGeometry3D.ts — exported so the Node lane asserts the
	 *  structure).  Built lazily on first 3D use; shared by every 3D chunk. */
	private geo3D: THREE.BufferGeometry | null = null;

	constructor(scene: THREE.Scene) { this.scene = scene; this.geo2D = new THREE.PlaneGeometry(1, 1); }

	private getGeo3D(): THREE.BufferGeometry {
		if (!this.geo3D) this.geo3D = buildTerrainGeo3D(CHUNK_N);
		return this.geo3D;
	}

	/** Acquire a mesh for the LEGACY bake path (ImageBitmap → MeshBasicMaterial.map).
	 *  2026-08-13 — pool is SHARED with the shader path, so a reused mesh can still hold a
	 *  ShaderMaterial clone (ignores .map → stale proc-mat content); swap in a real basic mat.
	 *  2026-08-23 — the `transparent`/`depthWrite` flags are now mode-dependent (mirrors the
	 *  shader path's Set15 P3 §3.7 handling): in 2D the overlay stays a TRANSPARENT billboard
	 *  (`transparent: true, depthWrite: false`, sorted in the transparent pass over the flat
	 *  map), but in 3D it must be OPAQUE (`depthWrite: true`) so it renders in the opaque pass
	 *  alongside the building meshes and is properly depth-occluded — otherwise the transparent
	 *  terrain renders AFTER the buildings and overdraws them even though its quad sits at
	 *  z = terrainOverlay (-1), geometrically BELOW the buildings' z=0+ surfaces. */
	acquire(bounds: Rect, texture: THREE.Texture, opacity: number, isPerspective: boolean): THREE.Mesh {
		let mesh = this.pool.pop();
		if (!mesh) {
			const mat = new THREE.MeshBasicMaterial({map: texture, transparent: true, depthWrite: false});
			mesh = new THREE.Mesh(this.geo2D, mat);
			this.scene.add(mesh);
		} else if (!(mesh.material instanceof THREE.MeshBasicMaterial)) {
			// Reusing a mesh that carried a shader-path ShaderMaterial — swap in a real
			// MeshBasicMaterial instead of mutating the wrong material type.
			mesh.material = new THREE.MeshBasicMaterial({transparent: true, depthWrite: false});
		}
		mesh.geometry = this.geo2D;
		const mat = mesh.material as THREE.MeshBasicMaterial;
		mat.map = texture;

		// Branch A ("overlay opacity" is applied in both 2d-mode and 3d-mode)
		//mat.opacity = opacity;
		
		// Branch B ("overlay opacity" is applied only in 2d-mode)
		mat.opacity = isPerspective ? 1 : opacity;
		// Mode-dependent transparency, matching the shader path's Set15 P3 §3.7 handling
		// (`mat.transparent = !isPerspective; mat.depthWrite = isPerspective;`).  Without this,
		// the legacy terrain applies opacity, which is odd/unexpected in 3d-mode)
		mat.transparent = !isPerspective;
		mat.depthWrite = isPerspective;

		mat.needsUpdate = true;
		mesh.position.set(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2, mapZIndexes.terrainOverlay);
		mesh.scale.set(bounds.width, bounds.height, 1);
		// Re-enable per-mesh frustum culling (shared pool with the 3D path).  Flat 2D quads have an
		// exact bounding sphere, so this is a genuine win; also clears the `false` a mesh may carry back from 3D.
		mesh.frustumCulled = true;
		mesh.visible = true;
		return mesh;
	}

	/** Acquire a mesh for the Set9 Phase 3 shader path.  Each chunk gets a CLONED ShaderMaterial
	 *  (own per-chunk uniforms: u_origin/u_geoNormal/u_baseAlbedo; the param UBO + program are
	 *  shared via the manager's template).  Returns the mesh with its per-chunk material set. */
	acquireShaderMaterial(bounds: Rect, material: THREE.ShaderMaterial): THREE.Mesh {
		let mesh = this.pool.pop();
		if (!mesh) {
			mesh = new THREE.Mesh(this.geo2D, material);
			this.scene.add(mesh);
		} else {
			mesh.geometry = this.geo2D;
			mesh.material = material;
		}
		mesh.position.set(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2, mapZIndexes.terrainOverlay);
		mesh.scale.set(bounds.width, bounds.height, 1);
		// Re-enable per-mesh frustum culling (shared pool with the 3D path — see `acquire`).
		mesh.frustumCulled = true;
		mesh.visible = true;
		return mesh;
	}

	/** Set15 P3 (§3.3/§3.7) — acquire a mesh for the 3D (perspective) shader path.  Uses the
	 *  SHARED subdivided+skirt geometry (displaced in the vertex shader by `u_displaceScale`),
	 *  positioned at world z = 0 (the vertex displacement sets z = elevation; the 2D
	 *  `mapZIndexes.terrainOverlay` offset has no meaning under a perspective camera).
	 *
	 *  CRITICAL (2026-08-17) — the 3D geometry spans local [0,1] (vertices at i/(N-1), j/(N-1)),
	 *  NOT the centered [-0.5,0.5] of the 2D `PlaneGeometry`.  So the mesh must sit at the chunk's
	 *  BOTTOM-LEFT (bounds.x, bounds.y), not its center — otherwise the whole chunk is shifted by
	 *  +width/2 in +x (east) and +y (north), i.e. the camera appears ~half-a-chunk-width to the
	 *  west/south of the terrain (a ~30 m offset at a ~60 m chunk).  With bottom-left positioning,
	 *  local (0,0)→world (bounds.x, bounds.y) and local (1,1)→(bounds.x+width, bounds.y+height),
	 *  matching both the quadtree bounds AND the fragment's `u_origin + vUvLocal * u_chunkSize`
	 *  world-XZ mapping (setChunkOrigin uses the same bounds.x/bounds.y). */
	acquireShaderMaterial3D(bounds: Rect, material: THREE.ShaderMaterial): THREE.Mesh {
		let mesh = this.pool.pop();
		if (!mesh) {
			mesh = new THREE.Mesh(this.getGeo3D(), material);
			this.scene.add(mesh);
		} else {
			mesh.geometry = this.getGeo3D();
			mesh.material = material;
		}
		// Bottom-left, not center — see the CRITICAL comment above (the [0,1]-spanning geometry).
		mesh.position.set(bounds.x, bounds.y, 0);
		mesh.scale.set(bounds.width, bounds.height, 1);
		// Set15.5 fix — DISABLE per-mesh frustum culling: the flat z=0 geometry's CPU bounding
		// sphere ignores the shader's vertex displacement, so at a grazing angle it drops a
		// barely-in-view chunk.  The quadtree viewportBounds cull is the real cull (cf. SceneryRenderer).
		mesh.frustumCulled = false;
		mesh.visible = true;
		return mesh;
	}

	release(mesh: THREE.Mesh) {
		mesh.visible = false;
		// Set15 P8 (§6.2) — clear shadow participation on release so a chunk reused from the pool
		// across a 2D↔3D mode switch (or a shadows-on↔off toggle) doesn't carry stale cast/receive
		// flags.  The flags are re-set on each acquire.  The per-chunk customDepthMaterial clone
		// RIDES ALONG with the mesh (reused on the next 3D acquire — setTerrainDepthUniforms
		// re-binds its lattice), so it is NOT nulled here; it is disposed when the pool is
		// disposed (see dispose()).
		mesh.castShadow = false;
		mesh.receiveShadow = false;
		this.pool.push(mesh);
	}

	/** Set15 P5 debug — the number of meshes currently in the pool (not active). */
	size(): number { return this.pool.length; }

	dispose() {
		for (const mesh of this.pool) {
			this.scene.remove(mesh);
			(mesh.material as THREE.Material).dispose();
			// Set15 P8 (§6.2) — dispose any per-chunk shadow-depth-material clone (shared GL
			// program, but each clone owns its uniforms).
			if (mesh.customDepthMaterial) (mesh.customDepthMaterial as THREE.Material).dispose();
		}
		this.pool = [];
		this.geo2D.dispose();
		if (this.geo3D) this.geo3D.dispose();
	}
}

export const TerrainRenderer = observer_mgl_r3((props: {map: DMap, mapState: MapState})=>{
	const {map, mapState} = props;
	const settings = mapState.settings;
	const terrain = settings.terrain;
	const gpu = settings.gpu;
	// Narrow to terrain-relevant fields so accidental access to non-terrain MapObject properties...
	// produces a compile error in this component (so we know to update StripMapObjForTerrain).
	// The underlying runtime objects are still full MapObjects; we cast back to MapObject only for ResolveObjPosInWorld.
	const mapObjects = GetMapObjects(map.id);

	// Collect active terrain-definition modifiers
	const terrainMods: Mod_TerrainDefinition[] = [];
	for (const obj of mapObjects) {
		for (const mod of obj.modifiers ?? []) {
			if (mod.enabled && mod.terrainDefinition) terrainMods.push(mod.terrainDefinition);
		}
	}

	// **Set15 P5 — the resolved Poisson solver config** (first enabled terrain mod).  The 3D
	// solve-domain knobs (`mode3D_workingGridRadius`, `mode3D_fineCellSize`, `ringStackEnabled_3d`,
	// …) moved here from `MapSettings_View3D` (2026-08-17) so all solve knobs
	// live on the terrain definition.  The renderer reads the near-field solve geometry from it;
	// the worker resolves the SAME config (`new PoissonSolverConfig(mod.constraintSettings.
	// poissonSolver)`), so the two agree.  Falls back to `new PoissonSolverConfig()` when no
	// terrain def is present (the 3D solve is moot then anyway).
	const resolvedPoissonCfg = (terrainMods[0]?.constraintSettings?.poissonSolver)
		? new PoissonSolverConfig(terrainMods[0].constraintSettings.poissonSolver)
		: new PoissonSolverConfig();

	// Set12 P2 — the per-map splat data is driven by `ObjPath.materialFeature` →
	// `collectMaterialRegions` (the demo fallback above renders when the map defines no
	// materialFeature paths of its own).  The Set9 per-map `assignments` were deleted (§6).
	// Re-collect per render body (cheap — a handful of paths; the signature block below handles
	// invalidation when the region set changes).  Runs AFTER `terrainObjects` is resolved (the
	// regions need the packaged `_meshSet_world` + `_curve_closed_resolved` geometry).

	// Collect terrain region rects (world-space AABB from position + shape_size)
	const terrainRegions: TerrainRegionRect[] = [];
	for (const obj of mapObjects) {
		for (const mod of obj.modifiers ?? []) {
			if (!mod.enabled || !mod.terrainRegion) continue;
			const pos = ResolveObjPosInWorld(obj as unknown as MapObject);
			const size = obj.shape_size;
			if (!pos || !size) continue;
			terrainRegions.push({
				terrainID: mod.terrainRegion.terrainID,
				rect: new Rect(pos.x - size.x / 2, pos.y - size.y / 2, size.x, size.y, false),
			});
		}
	}

	const activeTerrainIDs = new Set(terrainMods.map(m=>m.terrainID));

	// Collect V3 path-features via collectRasterFeatures. Path points are in absolute world coords;
	// fBm paths seeded with worldSeed so renderer + solver agree (same resolution as PathUI.tsx).
	// todo: confirm this logic still holds

	const terrainObjects: MapObject_ForTerrain[] = ResolveMapObjectsForTerrain(
		GetMapObjects(map.id),
		/*resolvePathOriginPathIDs:*/ true,
		/*keepPathMeshLocalPoints:*/ false,
		/*calcPathMeshWorldPoints:*/ true,
	);
	const terrainFeatures: RasterFeature[] = collectRasterFeatures(terrainObjects);
	const mapMaterialRegions = collectMaterialRegions(terrainObjects);

	// Resolve display config (colour ramp + hillshading) from first enabled TerrainDefinition mod.
	const firstMod = terrainMods[0];
	const gradients = firstMod?.heightColorGradients;
	const resolvedColorGrad = gradients && gradients.length > 0
		? TerrainHeightColorGradient.GetDefault(gradients)
		: new TerrainHeightColorGradient({name: "Default", isDefault: true});
	const resolvedShadingCfg = firstMod?.heightShadingConfig ?? new TerrainHeightShadingConfig();

	const {scene, camera, size, invalidate, gl} = useThree();
	const qtRef = useRef<TerrainQuadtree|null>(null);
	const poolRef = useRef<ChunkMeshPool|null>(null);
	const workerPoolRef = useRef<TerrainWorkerPool|null>(null);
	const texMapRef = useRef<Map<QuadtreeNode, THREE.Texture>>(new Map());
	/** Set9 Phase 3 — the procedural-materials manager (program cache + per-chunk uniform binding).
	 *  Created lazily when the shader path is enabled; disposed on unmount. */
	const materialManagerRef = useRef<TerrainMaterialManager|null>(null);
	/** Set9 Phase 3 — the template material + param UBO (shared across chunks).  Rebuilt when the
	 *  active recipe set changes (program cache dedupes by structural hash, D1). */
	const materialProgramRef = useRef<Awaited<ReturnType<TerrainMaterialManager["ensureProgram"]>> | null>(null);
	/** Set9 Phase 3 — per-chunk cloned ShaderMaterials, keyed by node (mirror of texMapRef).
	 *  Each clone owns its per-chunk uniforms; evicted with the chunk. */
	const chunkMaterialMapRef = useRef<Map<QuadtreeNode, THREE.ShaderMaterial>>(new Map());
	/** Set9 Phase 5 — per-chunk splat textures, keyed by node. Built from the worker's heights/
	 *  normals via rasterizeSplatFromArrays once per chunk; re-rasterised when texVersion bumps
	 *  (the texture queue's data-recomputed signal); evicted with the chunk. */
	const splatMapRef = useRef<Map<QuadtreeNode, {indexTex: THREE.DataTexture; weightTex: THREE.DataTexture; weightTex2: THREE.DataTexture; regionTex?: THREE.DataTexture; paramsTex?: THREE.DataTexture; slotDominance?: number[]; slotFeather?: number[]; slotSoftUnion?: number[]; slotNoiseAmp?: number[]; slotNoiseScale?: number[]; chunkRecipeIDs?: string[]; texVersion: number}>>(new Map());
	/** Guard 2 (Set7 §9.5) — the variant KEY (identity-set memo key) each chunk currently holds,
	 *  used to acquire/release the variant ROW on the manager (so eviction can reclaim rows no
	 *  longer referenced by a live chunk).  Evicted with the chunk (the release runs in onEvict). */
	const chunkVariantKeyMapRef = useRef<Map<QuadtreeNode, string>>(new Map());
	/** Set7 diagnostic — per-frame count of chunks that FALL BACK to variant row 0 (all-zeros →
	 *  black) because their identity-set variant wasn't recorded.  Exposed via lfTerrainDebug. */
	const chunkVariantRow0CountRef = useRef(0);
	/** 2026-08-26 — variant self-heal: identity sets (memo keys) with a pending ensureProgram
	 *  (see the frame loop's !chunkVariant branch — the per-set dedupe so a black region costs
	 *  one ensure, not one per chunk per frame). */
	const variantHealPendingRef = useRef<Set<string>>(new Set());
	/** 2026-08-29 — OPTION C per-chunk-program self-heal: sets (memo keys) with a pending
	 *  ensureProgram, deduped so an unknown per-chunk set costs ONE compile, not one per chunk
	 *  per frame.  Separate from variantHealPendingRef: a set needing its program healed is
	 *  never rendered this frame (SD3 hides it) until the program lands, so it never reaches the
	 *  variant branch (which only fires once the program already exists). */
	const programHealPendingRef = useRef<Set<string>>(new Set());
	/** Set9 — per-chunk geometric-normal lattice texture (u_geoNormal: the worker's 33×33
	 *  normals — slope_scale ALREADY applied, z-up).  Built once per texVersion; the shader's
	 *  matGeoNormal() fetches it per fragment → hillshade + mask-slope at FINE-CELL granularity
	 *  (the Phase-3 centre-sample uniform collapsed both to one slope per chunk).  Evicted with
	 *  the chunk. */
	const geoNormalMapRef = useRef<Map<QuadtreeNode, {tex: THREE.DataTexture; texVersion: number}>>(new Map());
	/** 2026-08-13 — per-chunk HEIGHT lattice texture (u_geoHeight: the worker's CHUNK_N×CHUNK_N
	 *  heights, R32F).  Built once per texVersion (mirror of geoNormalMapRef); the shader's
	 *  matGeoHeight() fetches it per fragment → the `geoHeight` primitive's elevation-reactive
	 *  colour (water depth).  Evicted with the chunk. */
	const geoHeightMapRef = useRef<Map<QuadtreeNode, {tex: THREE.DataTexture; texVersion: number}>>(new Map());
	/** Set15 P3 (§3.4) — per-chunk D-LESS height lattice texture (u_geoHeightGeom: the worker's
	 *  CHUNK_N×CHUNK_N `geoHeightGeom`, R32F).  The VERTEX shader displaces geometry by this in
	 *  3D (u_displaceScale-gated); in 2D it is bound but unused (displacement 0 — byte-identical).
	 *  Mirrors geoHeightMapRef (built per texVersion, evicted with the chunk). */
	const geoHeightGeomMapRef = useRef<Map<QuadtreeNode, {tex: THREE.DataTexture; texVersion: number}>>(new Map());
	/** Issue 2 (2026-08-08) — per-chunk ambient-weight mask texture (u_ambientWeightMask:
	 *  the worker's CHUNK_N×CHUNK_N multiplier lattice, R32F).  Built per texVersion when the
	 *  chunk carries a mask; the shader multiplies ∇D by it.  Evicted with the chunk. */
	const ambientWeightMapRef = useRef<Map<QuadtreeNode, {tex: THREE.DataTexture; texVersion: number}>>(new Map());
	/** Set9 Phase 5 — whether the splat program is active (mirrors `proceduralMaterials` +
	 *  the splat demo toggle; gated by the SAME flag so the old single-material path stays
	 *  reachable while splat data is still demo-driven). */
	const splatEnabledRef = useRef(false);
	/** Set9 Phase 7 — whether the region-params texture path is active.  Set12: wired in P2
	 *  (per-path `materialFeature` regions → the per-region params texture). */
	const regionParamsEnabledRef = useRef(false);
	/** Set9 Phase 6 — the splat recipe-id signature LAST warm-up'd.  When the live assignment set
	 *  changes to a new signature, the warm-up is re-scheduled (idle) so the new set's programs
	 *  are compiled before the first chunk needs them. */
	const warmUpSignatureRef = useRef("");
	/** Set9 Phase 7 — the FULL assignment-set signature last seen (recipe ids + rule shapes/values
	 *  + param modulations).  A change invalidates every chunk's rasterised splat textures (they
	 *  are pure functions of this set) + the live program, so the NEXT useFrame re-rasterises
	 *  (texVersion-keyed) + re-derives.  Compared in the render body (post-setTerrainSplatAssignments)
	 *  — see the `splatAssignmentsSignature` change block below. */
	const prevSplatAssignmentsSignatureRef = useRef("");
	/** Set12 P2 — the MATERIAL-REGION signature last seen (the region set is the rasterizer input
	 *  now).  A change (a path's materialFeature edited, a region added/removed) invalidates every
	 *  chunk's rasterised splat textures + the live program — see the `materialRegionsSignature`
	 *  change block below.  Distinct from the OLD assignment signature (kept for Set9 references):
	 *  the region set is derived from `ObjPath.materialFeature` each render. */
	const prevMaterialRegionsSignatureRef = useRef("");
	/** 2026-08-08 — the recipe param-VALUE signature last seen (each assignment recipe's resolved
	 *  param defaults — which carry the recipe's `paramOverrides`).  A params-only edit (slider/
	 *  color change in the recipe editor) changes this WITHOUT touching the assignment signature
	 *  (structure unchanged → splat textures + program stay valid).  The param values reach the
	 *  shader through the shared param UBO, which `ensureProgram` packs ONCE from the then-current
	 *  resolved defaults — so a param change left the old UBO values live (the reported bug: the
	 *  terrain doesn't reflect the edit until the page is reopened).  Compared in the render body;
	 *  a change re-packs the UBO via {@link TerrainMaterialManager.refreshParamValues} — see the
	 *  `paramOverridesSignature` change block below. */
	const prevParamOverridesSignatureRef = useRef("");
	/** Set9 Phase 3 — the per-frame light rig (recomputed when the shading config changes). */
	const lightRigRef = useRef<{lightDir: [number, number, number]; ambient: number; strength: number}>({lightDir: [0.7071, 0, 0.7071], ambient: 0.2, strength: 1.0});
	/** Set10 — the per-config ambient-detail uniform values (the sub-lattice band's fractal
	 *  parameters, from the first terrain mod's `ambient` + seed).  Recomputed when the mods
	 *  change; written to every chunk material's ambient uniforms alongside the light rig
	 *  (the u_amb* uniforms live on cloned materials, so each frame sets them per chunk). */
	const ambientDetailValuesRef = useRef<ReturnType<typeof ambientDetailUniformValues>>(
		ambientDetailUniformValues({}, 0),
	);
	/** Set9 Phase 3 — whether the shader path is active (mirrors MapSettings_Terrain.proceduralMaterials). */
	const proceduralMaterialsRef = useRef(false);
	const activeMeshesRef = useRef<THREE.Mesh[]>([]);
	/** Set15 P8 (§6.2) — the SHARED shadow-casting depth material template (displaced terrain
	 *  depth pass).  Cloned per 3D casting chunk (per-chunk u_geoHeightGeom); the GL program is
	 *  shared.  Lazily created on first 3D cast; null until then. */
	const shadowDepthMaterialRef = useRef<THREE.Material|null>(null);
	const chunksDirtyRef = useRef(false);
	const terrainModsRef = useRef<Mod_TerrainDefinition[]>([]);
	const slopeScaleRef = useRef(0);
	// Live mirror of `overlay_evalChunkBatchSize`; read fresh by the demand-pull helper (the
	// useEffect([]) loop captures the initial `terrain` closure). Pacing-only — no cache invalidation.
	const evalChunkBatchSizeRef = useRef(32);
	/** 2026-08-24 — live mirror of `overlay_heightSolverEnabled`.  When `false`, the off-thread eval
	 *  loop fills every chunk with the flat fallback height (no worker solve) so the terrain
	 *  materials appear near-instantly in 2D.  Mirrored into a ref because the loop effect captures
	 *  the initial `terrain` closure. */
	const heightSolverEnabledRef = useRef(true);
	/** 2026-08-24 — live mirror of `overlay_heightFallback` (the global height in metres used when the
	 *  height solver is disabled).  See {@link heightSolverEnabledRef}. */
	const heightFallbackRef = useRef(0);
	/** Set15.5 P3 (§5.3) — the LOD-coarsening factor threaded into/out of `TerrainQuadtree.update()`.
	 *  `update()` reads the previous frame's value (`get`) for its exponential-smoothing hysteresis
	 *  and writes the smoothed value back (`set`); the ref gives it stable state across frames. */
	const lodCoarseningRef = useRef(1);
	/** Set15.5 P6 — whether 3D hit the hard chunk ceiling and halted eval.  Written by
	 *  `TerrainQuadtree.update()` (via the `renderLimitHit` get/set) and surfaced in the Stats panel
	 *  as a red warning.  Observable so the warning UI can react; only meaningful under perspective. */
	const renderLimitHitRef = useRef(false);
	const colorGradRef = useRef(resolvedColorGrad);
	const shadingCfgRef = useRef(resolvedShadingCfg);
	const terrainRegionsRef = useRef<TerrainRegionRect[]>(terrainRegions);
	const activeTerrainIDsRef = useRef<Set<string>>(activeTerrainIDs);

	/** Map from mod-data hash → PreparedTerrainDataV3. Cleared when mods / features change. */
	const preparedStatesRef = useRef<Map<string, PreparedTerrainDataV3>>(new Map());
	/** Hashes of mods currently being prepared in a worker. */
	const preparingRef = useRef<Set<string>>(new Set());
	/** True when a camera move requires a working-grid re-solve before any eval can sample.
	 *  Set in `useFrame` on camera move; cleared on `solveTerrainDone`.
	 *  While true, dispatches one `solveTerrain` to worker 0 (guarded by `solveSentRef`); no evals sent. */
	const solveNeededRef = useRef(false);
	/** True once the `solveTerrain` message has been dispatched for the current `solveNeededRef`
	 *  window, so the `loop` ticker doesn't re-dispatch on every tick while awaiting the reply.
	 *  Cleared alongside `solveNeededRef` on `solveTerrainDone`. */
	const solveSentRef = useRef(false);
	/** 2026-08-03 rapid-zoom-queue fix — camera SIGNATURE of the in-flight `solveTerrain`: a DIFFERENT
	 *  signature dispatches a new solve IMMEDIATELY (worker aborts the prior in-flight one via its
	 *  per-V-cycle `signal.aborted` check); same-camera re-dispatches COALESCE. */
	const inFlightSolveCameraRef = useRef<{cameraView: {minX: number; minY: number; maxX: number; maxY: number}; fineCellSize: number} | null>(null);
	/** 2026-08-03 rapid-zoom-queue fix — monotonic per-dispatch attempt id, captured by each reply
	 *  callback; only the latest (``current === myId``) resets solve state + re-primes eval. A stale
	 *  reply ignores the reset, so a prior solve's slow reply can't clobber a rapid later move's state. */
	const solveAttemptIdRef = useRef(0);

	// ── Terrain-process timings (read by the map-UI Stats panel "Terrain Info" section) ──
	// Solve window = solveTerrain dispatch→reply; eval-fill window = first evalChunkBatch after a
	// solve settles → quadtree eval-queue drained.
	/** `performance.now()` at the `solveTerrain` dispatch; written to `terrainProcessStats.lastSolveDispatchedAt` and used to compute `lastSolveMs` on the reply. */
	const solveStartRef = useRef<number | null>(null);
	/** `performance.now()` when the current eval-fill window started, or `null` when none in flight. Finalised into `terrainProcessStats.lastEvalFillMs` when the quadtree eval queue drains (or the 3D hard-ceiling HALT leaves only the intentionally-unrendered far field queued — see `checkEvalFillDrained`) AND every dispatched batch has replied. */
	const evalFillStartRef = useRef<number | null>(null);
	/** In-flight `evalChunkBatch` dispatches (awaiting worker reply). The eval-fill window finalises only when the eval queue is empty AND this is 0. */
	const evalInFlightRef = useRef(0);
	/** 2026-09-01 materials-first — leaf nodes that were flat-filled by the always-on pre-pass
	 *  while a height solve was pending (`solveNeededRef` true).  Once the real solve settles,
	 *  these are re-enqueued (dirty_data + dirty_texture) so the true heights replace the flat
	 *  fill.  Cleared on `solveTerrainDone` / the prepare-reply, after re-enqueuing.  Not used in
	 *  solver-disabled FLAT-HEIGHT mode (there is never a solve to upgrade them). */
	const flatFilledForSolveRef = useRef<QuadtreeNode[]>([]);
	// (Tier-1 follow-on history: since **2026-07-28 solve-share** `prepareTerrain` routes to ONLY worker
	// 0; workers 1..N get the cache via BroadcastChannel. `evalChunk` still round-robins; a cache-miss
	// zero-fill can fire transiently until the import lands — next frame's re-dispatch finds it.)
	/** The current V3 RasterFeature[] (forwarded to prepareTerrain). */
	const terrainFeaturesRef = useRef<RasterFeature[]>(terrainFeatures);
	/** The current camera-view world AABB (forwarded to both prepareTerrain and evalChunk). */
	const cameraViewRef = useRef<{minX: number; minY: number; maxX: number; maxY: number}>({minX: -ROOT_HALF, minY: -ROOT_HALF, maxX: ROOT_HALF, maxY: ROOT_HALF});
	/** The current fine cell size (m). Updated each frame from the camera's worldToScreen. */
	const fineCellSizeRef = useRef<number>(5);
	/** Set15 P5 — the current solve mode: true = 3D (perspective), false = 2D. Updated each frame
	 *  from `viewInfo.isPerspective` and shipped to workers as `mode3D` so the ring-stack master
	 *  gate (`ringStackEnabled_3d` vs `ringStackEnabled_2d`) is picked per-mode. */
	const perspectiveRef = useRef<boolean>(false);
	/** Seed for the mod+features hash that groups cache identities. */
	const featuresHashRef = useRef<string>("");

	// **2026-07-28 evalChunk stale-camera-cancel regression workaround.** The demand-pull helper captured
	// `(cameraView, fineCellSize, slopeScale)` per `loop` tick, so stale chains fired
	// `refreshWorkingGridForCamera(OLD)` on a NEWER camera; per-dispatch `cameraGen` lets the worker refuse stale-camera `setCamera` (loud-fail guard on `cameraGen < state.lastCameraGen`).
	/** Monotonic per-camera-move version.  Bumped in `useFrame` every time the camera's
	 *  AABB changes from the previously-written tick (a true camera-settle edge).  Read
	 *  by `dispatchNextEvalNode` for the evalChunk dispatch's `cameraGen` field. */
	const cameraGenRef = useRef(0);
	/** Camera AABB + `fineCellSize` at last `cameraGen` bump.
	 *  Workers use the tagged `cameraGen` to reject stale-camera `setCamera` calls.
	 *  Only used for diagnostics. */
	const lastCameraSnapRef = useRef<{cameraView: {minX: number; minY: number; maxX: number; maxY: number}; fineCellSize: number}>({cameraView: {minX: -ROOT_HALF, minY: -ROOT_HALF, maxX: ROOT_HALF, maxY: ROOT_HALF}, fineCellSize: 5});

	// 2026-07-30 receiver-readiness ACK — per-worker latest imported (or freshly-solved) `cameraGen`. ALL
	// workers (incl. worker 0, the solver) start at -1 and climb on `receiverReady` ACKs (worker 0 ACKs
	// from `refreshWorkingGridForCamera` / `prepareTerrain`'s first solve). Reset to all `-1` on camera move; re-ACKed by each worker's next solve/import. See `workerReadyForGen`.
	const readyGenByWorkerRef = useRef<number[]>([]);
	/** True iff worker `i` ACK'd `receiverReady` for `gen` or later. (even worker0 must send that ACK)
	 *  Skips not-ready workers in prime + pull chain (share stays queued, chain NOT killed). */
	const workerReadyForGen = (i: number, gen: number): boolean=>{
		const arr = readyGenByWorkerRef.current;
		return (arr[i] ?? -1) >= gen;
	};

	// **2026-07-30 per-worker in-flight cap** — tracks `evalChunkBatch` dispatches per worker that have NOT
	// yet replied. A worker at/above `MAX_INFLIGHT_PER_WORKER` is skipped by `primeEvalAcrossAllWorkers`
	// (share stays queued) and refused by `redispatchSameWorker` (reply frees the slot and re-primes). Stops worker 0 — first to ACK after a camera move — from absorbing the whole eval queue before receivers 1..N import the shared solved grid ("only worker 0 runs evalChunkBatch" symptom).
	const inFlightByWorkerRef = useRef<number[]>([]);
	/** True iff worker `i` has >= `MAX_INFLIGHT_PER_WORKER` `evalChunkBatch` dispatches in flight.
	 *  Callers treat like `!workerReadyForGen`: skip, leave share queued. Reply decrement re-opens slot. */
	const workerAtInflightCap = (i: number): boolean=>{
		const arr = inFlightByWorkerRef.current;
		return (arr[i] ?? 0) >= MAX_INFLIGHT_PER_WORKER;
	};

	// **Texture-config cache stamp.**  When the bake config hasn't changed since the last
	// broadcast, per-chunk `evalChunk` messages omit the four config fields; workers use their
	// cached `setTextureConfig` slot instead, avoiding a structured-clone + re-derivation per chunk.
	const lastTexBakeConfigKeyRef = useRef<string>("");

	// Keep refs current so async callbacks always use the latest values
	terrainModsRef.current = terrainMods;
	// Set15 P2.5 (D3) — `slope_scale` RETIRED: pinned to 1.0.  The worker's central-difference
	// normals at slope_scale=1 ARE the true geometric normals of the displaced surface (which
	// P3 displaces by), so shading + silhouette agree.  Existing persisted `slope_scale` values
	// are silently dropped (the `viewRefinementPadding` rename precedent).  The `u_slopeScale`
	// uniform + `matAmbientSlopeScale()` seam is RETAINED and set to 1.0 so re-adding is one line.
	slopeScaleRef.current = 1.0;
	evalChunkBatchSizeRef.current = terrain.overlay_evalChunkBatchSize;
	// 2026-08-24 — live mirrors of the height-solver toggle + fallback height, read by the
	// off-thread eval loop (which captures the initial `terrain` closure).  When the solver is
	// disabled, the loop evaluates every chunk to the FLAT fallback height instead of dispatching
	// worker solve+eval — so pan/zoom material fills become near-instant (no Poisson solve).
	heightSolverEnabledRef.current = terrain.overlay_heightSolverEnabled;
	heightFallbackRef.current = terrain.overlay_heightFallback;
	colorGradRef.current = resolvedColorGrad;
	shadingCfgRef.current = resolvedShadingCfg;
	terrainRegionsRef.current = terrainRegions;
	activeTerrainIDsRef.current = activeTerrainIDs;
	terrainFeaturesRef.current = terrainFeatures;
	// Set9 Phase 3 — mirror the shader-path toggle + light rig into refs so the loop/useFrame
	// (which capture the initial closure) read fresh values without re-subscribing.
	proceduralMaterialsRef.current = terrain.proceduralMaterials;
	// Set9 Phase 5 — the splat is gated behind the SAME toggle for now (a demo default; Phase 7
	// makes it per-map data).  When on, ensureProgram assembles the ≤4-way splat shader.
	splatEnabledRef.current = terrain.proceduralMaterials;
	// Set9 Phase 7 — the region-params texture path is active when the map defines a
	// terrain-materials mod AND the region path is wired.  Set12 P2: ON — the region-params
	// texture path is the per-region `regionModulated`/`feather` mechanism; the region payload
	// (winning region per sample) is emitted by the P2 rasterizer.
	regionParamsEnabledRef.current = true;

	// Set12 debug hook — re-point the module-scope refs to the LIVE state each render body, so
	// `window.lfTerrainDebug.refresh()` reads current values.  (The hook itself lives in
	// TerrainDebug.ts; this is just the per-render push of the live refs.)
	terrainDebugUpdate({
		regions: terrainMaterialRegions,
		qt: qtRef.current,
		manager: materialManagerRef.current,
		splatMap: splatMapRef.current,
		chunkMaterialMap: chunkMaterialMapRef.current,
		baseRecipeID: terrainBaseRecipeID(map.id),
		splatEnabled: splatEnabledRef.current,
		materialProgSetHash: materialProgramRef.current?.setHash ?? "",
		gl: gl.getContext() as WebGL2RenderingContext,
		renderer: gl,
		activeMeshCount: activeMeshesRef.current.length,
		variantRow0FallbackCount: chunkVariantRow0CountRef.current,
	});
	// Set9 follow-up — surface the current per-set shader-program count (the Stats panel's
	// "Terrain programs" row).  The manager's cache size is the ground truth for "how many
	// distinct shaders this map generated"; `null` until the shader path has assembled one.
	RunInAction_Set(()=>terrainProcessStats.terrainProgramCount = materialManagerRef.current?.programCount ?? null);

	// **Set9 Phase 7 — assignment-change invalidation.**  `setTerrainSplatAssignments` above swaps
	// the module-level set on every map-object change (the UI's recipe-selector/rule editors mutate
	// the mod via ChangeMod).  But the CHUNKS' rendered output is derived from that set only at
	// splat-rasterise time — and that is CACHED keyed by `texVersion`, which an assignment change
	// does NOT bump (heights/normals unchanged → no eval, no dirty_texture → no texVersion).  So a
	// "change the region's target material" edit left the OLD index/weight textures bound: the
	// rasterised field still says the old recipe, and nothing ever pushed the new contents.
	//
	// Fix: compare the FULL assignment-set signature (recipe ids + rule shapes/values + param
	// modulations) against the last-seen render.  On a change, rebuild everything derived from it:
	//   1. drop every chunk material clone (they carry the OLD template's program + param UBO —
	//      three caches a material's program at first render, so a program swap alone would keep
	//      the old GLSL evaluating; the next useFrame re-clones from the new template);
	//   2. drop every cached splat texture (re-rasterised from the new set on the next useFrame);
	//   3. re-ensure the live program for the new set (a recipe change can alter the shader's eval
	//      slots; the D1 set-hash in ensureProgram recompiles when the set differs).  The OLD
	//      program keeps rendering until the new one is ready (no black flash); a stale resolve is
	//      dropped via the signature guard (rapid successive edits);
	//   4. bump `chunksDirtyRef` so the next invalidate interval repaints the frame.
	// (The existing warm-up path re-schedules off its own signature — `warmUpSignatureRef` — and
	//  needs no change; it fires on the next frame after this block.)
	//
	// Set12 P2: the OLD assignment set no longer exists (P1 deleted it).  The REAL signature
	// block is the REGION one below (`materialRegionsSignature`) — the Set9 assignment-signature
	// block above was removed (it referenced the deleted `terrainSplatAssignments`); the ref is
	// kept for the doc references only.  The region block owns the rebuild.

	// **Set12 P2 — material-REGION change invalidation.**  The actual rasterizer input is the
	// REGION set (`collectMaterialRegions` → module-level `terrainMaterialRegions`, published
	// below).  A region edit changes the chunk splats (pure functions of this set) + the live
	// program — the rebuild the Set9 block did, keyed by the region signature instead.  (A map
	// with no `materialFeature` paths keeps the demo regions; their signature is static, so this
	// block no-ops.)
	setTerrainMaterialRegions(mapMaterialRegions);
	const regionsSignature = materialRegionsSignature(terrainMaterialRegions, terrainBaseRecipeID(map.id));
	if (regionsSignature !== prevMaterialRegionsSignatureRef.current) {
		const changed = prevMaterialRegionsSignatureRef.current !== "";
		prevMaterialRegionsSignatureRef.current = regionsSignature;
		if (changed) {
			for (const mat of chunkMaterialMapRef.current.values()) mat.dispose();
			chunkMaterialMapRef.current.clear();
			for (const splat of splatMapRef.current.values()) {
				splat.indexTex.dispose(); splat.weightTex.dispose(); splat.weightTex2.dispose();
				if (splat.regionTex) { splat.regionTex.dispose(); splat.paramsTex!.dispose(); }
			}
			splatMapRef.current.clear();
			chunkVariantKeyMapRef.current.clear();
			const manager = materialManagerRef.current;
			if (manager && proceduralMaterialsRef.current) {
				// Guard 2 (§9.5) — every chunk variant was bulk-released above (the chunk clones
				// are disposed + the key map cleared); free the manager's rows so the re-ensure /
				// re-warm below re-packs fresh ones without unbounded growth.
				manager.releaseAllVariants();
				// Set12 P4 — per-chunk material sets: the re-ensure keeps the fast-path program
				// (splatRecipeIDs, the map-wide union) current for the single-set fallback, and
				// the warm-up (scheduled by the frame loop's warmSig — a region signature change
				// re-schedules it) enumerates the DISTINCT per-chunk sets so every chunk's own
				// program is compiled before its first appearance.  The chunk clones were
				// disposed above; the next useFrame re-clones each from its per-set program.
				manager.ensureProgram(splatRecipeIDs(terrainBaseRecipeID(map.id)), {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true}).then(prog=>{
					if (materialManagerRef.current !== manager) { prog.material.dispose(); return; }
					// Drop a stale resolve — a later region edit supersedes this program.
					if (materialRegionsSignature(terrainMaterialRegions, terrainBaseRecipeID(map.id)) !== regionsSignature) { prog.material.dispose(); return; }
					materialProgramRef.current = prog;
					for (const mat of chunkMaterialMapRef.current.values()) mat.dispose();
					chunkMaterialMapRef.current.clear();
					chunksDirtyRef.current = true;
				}).catch(err=>{
					console.error("[TerrainRenderer] procedural-materials program failed — falling back to the legacy bake path.", err);
				});
			}
			chunksDirtyRef.current = true;
		}
	}

	// **2026-08-08 — recipe param-OVERRIDE invalidation.**  The block above re-rasterises +
	// re-derives on an ASSIGNMENT change, but a params-only recipe edit (a slider/colour change
	// in the recipe editor — `paramOverrides`, structurally hash-identical, D1) leaves the
	// assignment signature UNCHANGED: the splat textures + program stay valid, yet the params
	// still wouldn't update, because the param values reach the shader through the SHARED param
	// UBO which `ensureProgram` packed ONCE from the then-current resolved defaults.  Nothing
	// ever re-packed it → the terrain kept showing the old values until the page reopened
	// (which rebuilt the manager → fresh UBO).
	//
	// Fix: compare the active recipes' resolved param-values against the last-seen signature.
	// On a change, re-resolve + re-pack the UBO in place (`refreshParamValues` — writes the new
	// values into the group's Uniforms; three.js re-uploads the changed members on the next
	// render, and the chunk CLONES share the group, so every chunk updates with zero program
	// recompile + zero splat re-rasterise).  A structural change (the assignment block) wins:
	// it already replaced the program, so this block must NOT write into the stale program's UBO
	// — `refreshParamValues`'s set-hash guard returns false for that case (no-op).
	//
	// Region-params caveat (Phase 7): when the region-params path is active, the per-chunk
	// `paramsTex` ALSO embeds the resolved defaults (as the fallback for absent
	// `paramModulations`) — a param-edit changing a `regionModulated` param's default makes
	// those textures stale too, and they're cached keyed by texVersion (unchanged).  Drop the
	// cached splat entries so the next useFrame re-rasterises them from the fresh resolved
	// state (a one-frame 33×33 cost per chunk; only on the region-params path).
	const paramOverridesSig = paramOverridesSignature(map.id);
	if (paramOverridesSig !== prevParamOverridesSignatureRef.current) {
		const changed = prevParamOverridesSignatureRef.current !== "";
		prevParamOverridesSignatureRef.current = paramOverridesSig;
		if (changed) {
			const manager = materialManagerRef.current;
			if (manager && materialProgramRef.current && proceduralMaterialsRef.current) {
				manager.refreshParamValues(splatRecipeIDs(terrainBaseRecipeID(map.id)), {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true}).then(refreshed=>{
					if (!refreshed) return; // structural change — the assignment block owns the rebuild
					// Region-params path: the cached paramsTex embeds the (now stale) resolved
					// defaults — drop every splat entry so the next useFrame rebuilds them.
					if (regionParamsEnabledRef.current) {
						for (const splat of splatMapRef.current.values()) {
							splat.indexTex.dispose(); splat.weightTex.dispose(); splat.weightTex2.dispose();
							if (splat.regionTex) { splat.regionTex.dispose(); splat.paramsTex!.dispose(); }
						}
						splatMapRef.current.clear();
					}
					chunksDirtyRef.current = true;
				}).catch(err=>{
					console.error("[TerrainRenderer] param-UBO refresh failed — keeping the previous param values.", err);
				});
			}
		}
	}
	lightRigRef.current = {
		lightDir: computeLightDirJS(resolvedShadingCfg.light_azimuth, resolvedShadingCfg.light_altitude),
		ambient: resolvedShadingCfg.ambient,
		strength: resolvedShadingCfg.strength,
	};
	// Set10 — the ambient-detail fractal parameters follow the FIRST terrain mod's ambient
	// config + seed (the same mod the worker's makeAmbientCtx uses, so the shader band continues
	// the CPU fractal's parameters — the noise-type mismatch is the accepted §5.2 decision (a)).
	ambientDetailValuesRef.current = ambientDetailUniformValues(
		firstMod?.ambient ?? {},
		firstMod?.seed ?? 0,
	);
	{
		// Cheap feature-set signature (kind + first/last few footprint coord sums). The full
		// JSON.stringify(features) is heavier than necessary for a frame loop; this catches any
		// feature-set change coarsely and the worker's cacheKey does the strict check.
		terrainFeatures.forEach((f, i)=>{}); // keep exhaustive forEach available for future debugging
		featuresHashRef.current = featuresSliceSignature(terrainFeatures);
	}

	// Continuous chunk-evaluation and texture-generation loop — fully off the render thread.
	useEffect(()=>{
		let cancelled = false;
		const loop = ()=>{
			if (cancelled) return;
			const qt = qtRef.current;
			const wp = workerPoolRef.current;
			const mods = terrainModsRef.current;

			if (qt && wp && mods.length > 0) {
				// 2026-08-24 — when the height solver is DISABLED, skip the worker `prepareTerrain`
				// solve entirely (the flat-height eval below fills chunks without a prepared state).
				// Kept inside the `qt && wp` guard so the flat-height path still runs only when the
				// worker pool + quadtree exist.
				if (heightSolverEnabledRef.current) {
					// Dispatch prepareTerrain for any mod that hasn't been prepared yet (or whose
					// features changed). The cache key folds mod + features + world bbox; the worker
					// keeps the solved grids, so a re-dispatch only triggers on a real change.
					for (const mod of mods) {
						const modPlain = MobXTreeToPlainTree(mod) as Mod_TerrainDefinition;
						const features = terrainFeaturesRef.current;
						const worldBBox = worldBBoxForFeatures(features);
						const hash = JSON.stringify(modPlain) + featuresHashRef.current + JSON.stringify(worldBBox);
						// Re-prepare when: cache miss AND not already preparing. Re-dispatches when
						// a feature-set change modifies the hash, since the previous prepared token
						// is keyed under the old hash (and therefore dropped from preparedStatesRef).
						if (!preparedStatesRef.current.has(hash) && !preparingRef.current.has(hash)) {
							preparingRef.current.add(hash);
							const cameraView = cameraViewRef.current;
							const fineCellSize = fineCellSizeRef.current;
							// **2026-07-28 stale-camera-cancel workaround** — stamp the live `cameraGen` (monotonic per-camera-move
							// version) onto every `prepareTerrain`; a later `evalChunk` whose `cameraGen` is BELOW the worker's
							// known-solved gen is a stale-camera pull-chain iteration — the worker loud-fails rather than re-solving.
							const cameraGen = cameraGenRef.current;
							// v3.1: include `overlayEnabled` so the worker attaches the cell snapshot
							// to `PreparedTerrainDataV3` when the constraint-cell overlay is on.
							const overlayEnabled = terrain.overlay_showConstraintCells;
							// Set6: dispatch per-V-cycle-phase backend selection + smoother preference verbatim.
							const componentBackends = gpu;
							const preferredSmootherVariant = gpu.preferredSmootherVariant;
							// Mirror worker's ensureBackendsInit for renderer-side diagnostics;
							// cast via unknown since MapSettings_GPU has no index signature.
							const selectionAsRecord = componentBackends as unknown as Record<string, unknown>;
							const wantsWebGPU = Object.values(selectionAsRecord).some(b=>b === "webGPU");
							const wantsWebGL = Object.values(selectionAsRecord).some(b=>b === "webGL");
							// **2026-07-28 solve-share** — route `prepareTerrain` to ONLY worker 0 (the solver). It builds the
							// TwoGridCache then publishes solved grids on a `BroadcastChannel` so workers 1..N import them and skip
							// the V-cycle solve (broadcast-to-all caused CPU/memory contention; the `prepareds.length > 0` gate blocks evalChunk until worker 0's reply; `workerCount == 1` = prior single-worker path). 2026-08-02 — forward user-configured `macroCellSizeTarget` (poissonSolver block) as `macroCellSize` (wire name kept for schema backward-compat) → `solveMacroGrid`; undefined/0 → worker falls back to `Math.max(worldExtent / 20, fineCellSize * 2)` (preserves all maps).
							const userMacroCellSize = modPlain.constraintSettings?.poissonSolver?.macroCellSizeTarget;
							// Time the FULL worker-0 solve round-trip (macro + working-grid build for the current camera).
							// This covers the initial load + feature/config-driven re-solves, so the "Solve" stat
							// populates even when no camera move ever fires a standalone `solveTerrain` (e.g. a locked
							// 3D viewpoint).  Captured in a LOCAL so it never clashes with `dispatchSolveTerrain`'s own
							// `solveStartRef`; the shared `lastSolveDispatchedAt` still drives the live "in flight" display.
							const prepareSolveStart = performance.now();
							RunInAction_Set(()=>terrainProcessStats.lastSolveDispatchedAt = prepareSolveStart);
							wp.dispatchToWorker(
								0,
								{type: "prepareTerrain", mod: modPlain, features, worldBBox, cameraView, fineCellSize, cameraGen, overlayEnabled, componentBackends, preferredSmootherVariant, workerCount: wp.workerCount, channelToken: wp.channelToken, mode3D: perspectiveRef.current,
								...(userMacroCellSize && userMacroCellSize > 0 ? {macroCellSize: userMacroCellSize} : {})} as Record<string, unknown>,
								[],
								response=>{
									if (cancelled) return;
									preparingRef.current.delete(hash);
									// 2026-07-25 silent-CPU-fallback diagnosis — forward the worker's `solverDiagnostics` to the
									// main-thread console (Chromium buries worker logs in a separate "thread" view). Worker 0 is the sole
									// solver (solve-share 2026-07-28), so its counters are canonical; workers 1..N report `solveShare_receivedGrids > 0` instead, queryable via `getDiagnostics` for per-worker divergence.
									const diag = response?.solverDiagnostics as Record<string, unknown> | undefined;
									if (diag) {
										const outcome = diag.gpuInitOutcome;
										const cpuNoBackend = diag.solveDispatch_cpuNoBackend;
										const webglOutcome = diag.webglInitOutcome;
										if (wantsWebGPU && (outcome !== "ok" || (cpuNoBackend as number) > 0)) {
											console.warn(
												`%c[TerrainRenderer] worker replied to prepareTerrain with WebGPU silent CPU fallback.`,
												"color:#e34;font-weight:bold",
												diag,
											);
										} else if (wantsWebGPU) {
											console.info(
												`%c[TerrainRenderer] worker replied to prepareTerrain with WebGPU dispatcher state OK.`,
												"color:#48c;font-weight:bold",
												diag,
											);
										} else if (wantsWebGL && webglOutcome !== "ok") {
											// **WebGL port Phase 1 (2026-07-26)** — at least one selector names "webGL" but init failed silently
											// (no OffscreenCanvas, no EXT_color_buffer_float, null getContext). WebGL's surface is "init or skip",
											// so the `webglOutcome !== "ok"` selector is the smoking-gun for WebGL-after-init-failed.
											console.warn(
												`%c[TerrainRenderer] worker replied to prepareTerrain with WebGL silent CPU fallback (init failed).`,
												"color:#e94;font-weight:bold",
												diag,
											);
										} else if (wantsWebGL) {
											// **WebGL Step 2 path (Set6 Phase 5, live since 2026-07-26).**  At least one selector names "webGL"
											// and init succeeded. When prolong + residual + restrict + galSmoother are ALL "webGL",
											// `WebGLSolverBackend.solveAsyncLocked` runs the real Step 2 V-cycle `solveAsyncFullVCycle_GL`; CPU fallback only on a GL dispatch throw (the `solveAsyncLocked` catch) or a malformed coarse level.
											console.info(
												`%c[TerrainRenderer] worker replied to prepareTerrain with WebGL dispatcher state OK (Step 2 V-cycle path active; CPU fallback only on GL error).`,
												"color:#4c8;font-weight:bold",
												diag,
											);
										} else {
											// No selector names a non-CPU backend — all phases routed to CPU reference path.
											// Logged so the user sees this selection is the cause (not a worker init failure).
											console.info(
												`%c[TerrainRenderer] prepareTerrain dispatched with all-CPU componentBackends selection — no GPU/WebGL backend requested.`,
												"color:#888",
												diag,
											);
										}
									} else {
										// No `solverDiagnostics` block — stale worker bundle (rspack didn't rebuild)
										// OR worker hit an early error before reaching the diagnostics-forward block.
										console.warn(
											`%c[TerrainRenderer] worker replied to prepareTerrain WITHOUT a solverDiagnostics block — worker bundle is stale OR an early error swallowed the reply path.`,
											"color:#e34;font-weight:bold",
											{wantsWebGPU, wantsWebGL, responseKeys: Object.keys(response ?? {})},
										);
									}
									// Worker 0 is the solver; workers 1..N get their cache via BroadcastChannel (worker 0 publishes after solve).
									// Only worker 0 replies to this dispatch — snapshots come along on its token.
									const prepared = response?.prepared as PreparedTerrainDataV3 | undefined;
									if (prepared) {
										preparedStatesRef.current.set(hash, prepared);
										terrainPreparedData_current = preparedStatesRef.current;
										terrainFeatures_current = terrainFeaturesRef.current;
										// Set15 P5 — surface the ACTUAL solved cell size (worker's
										// `working.effectiveCellSize`); the real near-field resolution.
										RunInAction_Set(()=>terrainProcessStats.fineCellSize_final = prepared.solvedCellSize ?? null);
										RunInAction_Set(()=>terrainDataVersion.set(terrainDataVersion.get() + 1));
										// Now that we have prepared data, dirty all chunks so they re-evaluate
										qtRef.current?.markAllDirty();
										chunksDirtyRef.current = true;
										// Materials-first (2026-09-01): `markAllDirty` above re-enqueues every
										// chunk (including any flat-filled during the prepare-pending window) for
										// a fresh real-height eval, so the flat fill is upgraded here too — clear
										// the tracker so a later solveTerrain reply doesn't re-mark stale nodes.
										flatFilledForSolveRef.current = [];
										// prepareTerrain's solve + broadcast have settled — no solve pending.
										solveNeededRef.current = false;
										solveSentRef.current = false;
										// 2026-08-03 rapid-zoom-queue fix — also clear the in-flight solve marker so a
										// subsequent `dispatchSolveTerrain` does not think a solve is still in flight
										// from before the prepareTerrain re-dispatch.
										inFlightSolveCameraRef.current = null;
										// prepareTerrain's internal solve settled without the `solveTerrain` dispatch path — reset the eval-fill window so the next first-eval opens a fresh one.
										evalFillStartRef.current = null;
										RunInAction_Set(()=>terrainProcessStats.lastEvalFillAt = null);
										// Record the solve round-trip (dispatch→reply) for this prepare — mirrors
										// `dispatchSolveTerrain`'s reply.  Only clear the in-flight marker if it's still
										// this prepare's (a newer `solveTerrain` may own it by now).
										RunInAction_Set(()=>{
											terrainProcessStats.lastSolveMs = performance.now() - prepareSolveStart;
											if (terrainProcessStats.lastSolveDispatchedAt === prepareSolveStart) {
												terrainProcessStats.lastSolveDispatchedAt = null;
											}
										});
									}
								},
							);
						}
					}
				} // end if (heightSolverEnabledRef.current) — skip the worker solve entirely in flat-height mode

				// Collect prepared tokens for all current mods (a subset if some are still preparing). Same hash key as above (modPlain + features + worldBBox).
				// **Set8 fix — extracted as a helper so `dispatchBatchToWorker` can re-read the LIVE `prepareds` at dispatch time** — the worker-local pull chain fires across ticks;
				// a toggle/feature-edit that clears `preparedStatesRef` mid-chain makes the closure-captured `prepareds` stale ("toggle shows stale heights until a camera move" symptom).
				const collectPrepareds = (): PreparedTerrainDataV3[]=>{
					const out: PreparedTerrainDataV3[] = [];
					for (const mod of mods) {
						const modPlain = MobXTreeToPlainTree(mod) as Mod_TerrainDefinition;
						const features = terrainFeaturesRef.current;
						const worldBBox = worldBBoxForFeatures(features);
						const hash = JSON.stringify(modPlain) + featuresHashRef.current + JSON.stringify(worldBBox);
						const p = preparedStatesRef.current.get(hash);
						if (p) out.push(p);
					}
					return out;
				};
				const prepareds = collectPrepareds();

				// Materials-first / heights-later (2026-09-01):
				// Always fill not-yet-evaluated queued leaf chunks with the flat fallback height
				// (`overlay_heightFallback`) + up-normals FIRST, BEFORE waiting on the Poisson
				// height solve.  This makes the terrain MATERIALS (per-chunk program set, splat
				// raster, geo textures) appear near-instantly — the `useFrame` texture-build block
				// reads `texVersion >= 1` (set here via `applyWorkerResult`) and renders a
				// geometry-flat chunk with the CORRECT per-chunk materials.  The real height solve
				// then lands as a DELAYED non-blocking upgrade: its `evalChunkBatch` reply calls
				// `applyWorkerResult` again, bumping `texVersion`, and the splat/geo builders
				// re-rasterise + re-bind from the true heights automatically (no black/blank frames).
				//
				// Conditions for the flat pre-pass:
				//  - always when the solver is DISABLED (`!heightSolverEnabledRef`) — the existing
				//    2026-08-24 FLAT-HEIGHT mode, unchanged;
				//  - when the solver is ENABLED but a solve is pending (`solveNeededRef`) OR no
				//    prepared token exists yet (`prepareds.length === 0`), so the flat fill covers
				//    the slow "waiting for the solve" window — this is the new materials-first path.
				// In both cases the flat fill is best-effort: chunks already evaluated (texVersion
				// >= 1) are left alone (they keep their real heights), and the queue semantics /
				// LRU budget / 3D halt ceiling are preserved (we only dequeue what
				// `dequeueForWorker` hands us).
				const doFlatPrePass = !heightSolverEnabledRef.current || solveNeededRef.current || prepareds.length === 0;
				if (doFlatPrePass) {
					// ── FLAT-HEIGHT eval ───────────────────────────────────────────────────────
					// Fill queued leaf chunks with the constant fallback height + up-normals,
					// WITHOUT dispatching a worker solve for them.  Reuses `applyWorkerResult` so
					// dirty_texture + texVersion + the texture queue are advanced exactly as a real
					// eval would — the texture-build block below then bakes (legacy) or drains
					// (shader path) them identically.
					//
					// 2026-09-01 mode split:
					//  - 2D (`!perspectiveRef.current`): flatten EVERYTHING queued, including
					//    already-evaluated chunks, during the solve — this is the original
					//    materials-first improvement (2D is a flat orthographic view; flattened
					//    heights only drive colours/materials, so re-flattening is harmless and
					//    gives instant chunk/material rendering while the solve runs).
					//  - 3D (`perspectiveRef.current`): ONLY flat-fill chunks that have NEVER been
					//    evaluated (`texVersion === 0`); leave already-evaluated chunks untouched
					//    in the eval queue.  An already-evaluated chunk carries REAL solved heights
					//    — e.g. after `markAllDirty()` re-enqueues it on a camera move / viewpoint
					//    switch — and must KEEP those heights until the new solve's eval lands
					//    (otherwise a ~200 m solved chunk would be flattened to the ~20 m fallback
					//    and drop below the 3D viewport: the "chunks disappear" regression).
					const flatH = heightFallbackRef.current;
					const n2 = CHUNK_N * CHUNK_N;
					const flatNodes = perspectiveRef.current
						? qt.dequeueForFlatFill(evalChunkBatchSizeRef.current)
						: qt.dequeueForWorker(evalChunkBatchSizeRef.current);
					// Materials-first (2026-09-01): when this flat fill is covering the SOLVE-pending
					// window (solver enabled, `solveNeededRef` true or no prepared token yet), remember
					// which nodes were flat-filled so the real solve can upgrade them later.
					const trackForUpgrade = heightSolverEnabledRef.current;
					for (const node of flatNodes) {
						// Per-node arrays (each chunk owns its lattices, matching the worker's
						// transfer contract — no aliasing between chunks).
						const flatHeights = new Float32Array(n2).fill(flatH);
						const flatNormals = new Float32Array(n2 * 3);
						for (let i = 0; i < n2; i++) flatNormals[i * 3 + 2] = 1; // up-normal (0,0,1)
						const flatGeoHeightGeom = new Float32Array(n2).fill(flatH);
						qt.applyWorkerResult(node, flatHeights, flatNormals, false, null, flatGeoHeightGeom);
						if (trackForUpgrade) flatFilledForSolveRef.current.push(node);
					}
					if (flatNodes.length > 0) {
						// Fresh (flat) heights landed — bump so the 3D camera re-derives eye height
						// against the fallback elevation.
						RunInAction_Set(()=>terrainDataVersion.set(terrainDataVersion.get() + 1));
						chunksDirtyRef.current = true;
					}
				}

				// Run the real worker solve + height eval once we have at least one prepared state.
				// NOTE: this branch must run even while a solve is pending (`solveNeededRef` true) —
				// `primeEvalAcrossAllWorkers` at its end is what DISPATCHES `solveTerrain` for a
				// pending solve (it then returns without evaling).  The flat pre-pass above has
				// already covered the waiting window with materials-first rendering; when the solve
				// settles its reply re-enqueues the flat-filled leaves so the real heights land on top.
				if (heightSolverEnabledRef.current && prepareds.length > 0) {
						// --- height+normal eval ---

						// Inline texture bake: capture cfg refs once per tick so eval dispatch
						// carries withTexture:true and the worker bakes the ImageBitmap in the same
						// round-trip (collapses prior evalChunk->buildTexture two-hop cycle).
						const sc = shadingCfgRef.current;
						const texBakeConfig = {
							regions: terrainRegionsRef.current.map(r=>({
								tid: r.terrainID, rx: r.rect.x, ry: r.rect.y, rw: r.rect.width, rh: r.rect.height,
							})),
							activeTerrainIDs: [...activeTerrainIDsRef.current],
							colorStops: MobXTreeToPlainTree(colorGradRef.current.stops),
							shadingCfg: {light_azimuth: sc.light_azimuth, light_altitude: sc.light_altitude, ambient: sc.ambient, strength: sc.strength},
						};
						// Broadcast `setTextureConfig` once per config change so each worker caches a
						// TextureBakeContext (built once, reused per chunk). Skipped when key matches (steady state).
						const texBakeConfigKey = JSON.stringify(texBakeConfig);
						if (texBakeConfigKey !== lastTexBakeConfigKeyRef.current) {
							lastTexBakeConfigKeyRef.current = texBakeConfigKey;
							// Fire-and-forget: no reply expected, so `dispatchBroadcast` would leak slots.
							// Each worker builds+caches a `TextureBakeContext`; subsequent evalChunk bakes use it.
							wp.dispatchFireAndForgetBroadcast(
								{type: "setTextureConfig", ...texBakeConfig} as Record<string, unknown>,
							);
						}

						/** Dispatch one `evalChunkBatch` to a SPECIFIC worker.  Extracted so the per-settle demand-pull helper and the prime-time multi-worker splitter share one reply-applicator.
						 *  **Set8 fix — re-reads `prepareds` from the LIVE `preparedStatesRef` at dispatch time, NOT from the `loop`-tick closure.** The worker-local pull chain (`redispatchSameWorker` -> `dispatchBatchToWorker`) fires across ticks; a toggle/feature-edit that clears `preparedStatesRef` + re-prepares mid-chain makes the closure-captured `prepareds` STALE (old cacheKey -> old ctx -> old heights — "toggle shows stale heights until a camera move"; the stale pull chain drains the queue before the new prepare's `markAllDirty` re-enqueue produces new heights). Re-reading ensures every dispatch uses the latest tokens. */
						const dispatchBatchToWorker = (workerIdx: number, nodes: QuadtreeNode[]): void=>{
							if (cancelled || nodes.length === 0) return;
							const livePrepareds = collectPrepareds();
							if (livePrepareds.length === 0) return;  // cleared mid-flight — chunks stay dirty, next tick re-primes
							const chunks = nodes.map(node=>({
								x: node.bounds.x, y: node.bounds.y,
								w: node.bounds.width, h: node.bounds.height,
							}));
							// Fresh per-dispatch reads (NOT closure-captured at prime time).  `cameraGen`
							// is the monotonic per-camera-move version the worker uses to detect a
							// stale-camera `evalChunk` reaching it after the renderer advanced.
							const cameraView = cameraViewRef.current;
							const fineCellSize = fineCellSizeRef.current;
							const cameraGen = cameraGenRef.current;
							// **Set8 — read `slopeScale` from the ref at dispatch time, NOT from the `loop`-tick closure.** Same stale-pull-chain class as `prepareds`: a shading config edit (part of `dataKey`) can clear `preparedStatesRef` mid-chain; the closure-captured `slopeScale` would bake wrong normals for in-flight chunks.
							const liveSlopeScale = slopeScaleRef.current;
							// Start the eval-fill window on the first dispatch after a solve settles / a camera move re-primes. `evalFillStartRef` is reset on `solveTerrainDone` and on a camera move.
							if (evalFillStartRef.current == null) {
								evalFillStartRef.current = performance.now();
								RunInAction_Set(()=>terrainProcessStats.lastEvalFillAt = evalFillStartRef.current);
							}
							// Set15 P5 debug — persistent cumulative eval-dispatch counter (always-on).
							RunInAction_Set(()=>terrainProcessStats.evalDispatchesSent++);
							evalInFlightRef.current++;
							// Bumped atomically with the dispatch so the cap check in primeEval/redispatch
							// sees it in-flight the moment it's posted. Decremented in the reply callback.
							inFlightByWorkerRef.current[workerIdx] = (inFlightByWorkerRef.current[workerIdx] ?? 0) + 1;
							wp.dispatchToWorker(
								workerIdx,
								{type: "evalChunkBatch", chunks, prepareds: livePrepareds, cameraView, fineCellSize, slopeScale: liveSlopeScale, cameraGen, mode3D: perspectiveRef.current,
									// Inline bake: ask the worker to bake textures in the same reply (Opt D —
									// config is cached worker-side via setTextureConfig; no per-chunk config fields).
									// Set9 Phase 3 — the shader path skips the bake (heights + normals suffice);
									// the worker then returns no bitmaps and we close them (see the reply handler).
									withTexture: !proceduralMaterialsRef.current} as Record<string, unknown>,
								[],
								result=>{
									if (cancelled) return;
									evalInFlightRef.current--;
									inFlightByWorkerRef.current[workerIdx] = Math.max(0, (inFlightByWorkerRef.current[workerIdx] ?? 0) - 1);
									// Receiver-waiting fast path: a receiver's cache was stale for the current
									// camera (shouldn't happen while the solve gate is set, but guard against races).
									// Re-enqueue the nodes once WITHOUT the zero-fill allocation; don't re-dispatch.
									if (result.receiverWaiting === true) {
										for (const node of nodes) {
											qt.applyWorkerResult(node, undefined as unknown as Float32Array, undefined as unknown as Float32Array, true);
										}
										chunksDirtyRef.current = true;
										// The re-enqueued nodes need re-dispatch.  `redispatchSameWorker` continues the
										// pull chain on THIS worker (it just freed a slot), matching the normal path;
										// `checkEvalFillDrained` finalises the eval-fill window when the queue has
										// genuinely drained AND nothing is in flight.  Pre-fix, this fast path returned
										// WITHOUT either call: if the last outstanding batch was a receiver-waiting
										// reply, the eval-fill window was never finalised → `lastEvalFillAt` stayed set
										// → the Stats "Eval/Sample" label counted up "in flight…" forever even after the
										// (genuinely complete) re-enqueued work drained on a later tick.  And in the
										// persistent case (a receiver stuck unready for the current gen) the re-enqueue
										// + re-dispatch loop likewise never settled the window.
										redispatchSameWorker(workerIdx);
										// Count this reply so the `evalRepliesApplied` diagnostic tracks
										// `evalDispatchesSent` (the receiverWaiting reply is a real reply that
										// freed an in-flight slot, even though it re-enqueued the nodes).
										RunInAction_Set(()=>terrainProcessStats.evalRepliesApplied++);
										checkEvalFillDrained();
										return;
									}
									// Zip the parallel result arrays with the dispatched `nodes[]` (2026-07-25 abort
									// re-enqueue path + 2026-07-28 inline-bake path).
									const heightsArr = result.heightsArr as Float32Array[];
									const normalsArr = result.normalsArr as Float32Array[];
									const geoHeightGeomArr = result.geoHeightGeomArr as Float32Array[];
									const ambientWeightMasks = result.ambientWeightMasks as (Float32Array | null)[];
									const abortedArr = result.abortedArr as boolean[];
									const bitmaps = result.bitmaps as (ImageBitmap | null)[];
									for (let i = 0; i < nodes.length; i++) {
										const node = nodes[i];
										qt.applyWorkerResult(node, heightsArr[i], normalsArr[i], abortedArr[i], ambientWeightMasks?.[i], geoHeightGeomArr[i]);
										// Set9 Phase 3 — the shader path needs NO bitmap: heights + normals (now
										// on node.chunk) feed the per-chunk uniforms in useFrame.  Close any
										// bitmap the worker still baked (the dispatch still carries withTexture
										// so the worker's inline-bake path stays exercised for the legacy toggle).
										if (proceduralMaterialsRef.current) {
											const bitmap = bitmaps[i];
											if (bitmap) bitmap.close();
											continue;
										}
										const bitmap = bitmaps[i];
										if (bitmap) {
											if (node.chunk && qt.applyInlineTextureResult(node)) {
												const tex = bitmapToTexture(bitmap);
												texMapRef.current.get(node)?.dispose();
												texMapRef.current.set(node, tex);
												node.chunk.onEvict = ()=>{
													texMapRef.current.get(node)?.dispose();
													texMapRef.current.delete(node);
												};
											} else {
												bitmap.close();
											}
										}
									}
									chunksDirtyRef.current = true;
									// Set15 P5 — bump the terrain-data version when a batch of chunks gets
									// fresh heights.  `MapSurface`'s 3D camera reads `terrainDataVersion` (an
									// observable) so it can recompute the eye height once the anchor chunk's
									// elevation is known — otherwise the camera stays at its initial
									// flat-0 eye height even after the terrain solves (the "buried in a peak"
									// bug: a camera built before the solve sits at z=eyeHeight while a 2215 m
									// mountain at the anchor swallows it → only the skybox renders).
									if (nodes.some(n=>!abortedArr[nodes.indexOf(n)])) {
										RunInAction_Set(()=>terrainDataVersion.set(terrainDataVersion.get() + 1));
									}
									// Set15 P5 debug — persistent cumulative eval-reply-applied counter (always-on).
									RunInAction_Set(()=>terrainProcessStats.evalRepliesApplied++);
									// Re-dispatch to THIS SAME worker (now free) — worker-local pull chain.
									redispatchSameWorker(workerIdx);
									// Finalise the eval-fill window once the last in-flight batch has replied AND the queue is drained (true end of fill, not just queue-empty).
									checkEvalFillDrained();
								},
							);
						};

						/** Dispatch a standalone `solveTerrain` message to worker 0. 2026-08-03 rapid-zoom-queue fix —
						 *  permits a NEW dispatch while a prior solve is in flight when the LATEST camera signature differs; the
						 *  worker-side `refreshWorkingGridForCamera` aborts the prior `AbortController` (its per-V-cycle `signal.aborted` check throws `SolveAbortedError` quickly). Same-camera re-dispatch COALESCES (`camChanged` no-ops on a camera matching `state.lastCameraView`, TerrainWorker.ts ~1425). A monotonic `solveAttemptId` is captured by the reply callback; a stale prior reply IGNORES the `solveNeededRef=false` reset + eval re-prime (the newer in-flight dispatch owns the state). */
						const dispatchSolveTerrain = (): void=>{
							if (cancelled) return;
							const cameraView = cameraViewRef.current;
							const fineCellSize = fineCellSizeRef.current;
							// Detect whether a solve is already in flight AND whether its camera signature
							// matches the latest.  `inFlightSolveCameraRef` is set below when a dispatch goes
							// out and cleared in the (latest) reply callback.
							const inflight = inFlightSolveCameraRef.current;
							if (inflight != null) {
								// A solve IS in flight.  Compare camera signatures with an epsilon matched to
								// the worker's `rectsClose` helper (1e-3) so a sub-millimeter drift doesn't
								// masquerade as a distinct camera and trigger a redundant abort.
								const sameView =
									Math.abs(inflight.cameraView.minX - cameraView.minX) < 1e-3
									&& Math.abs(inflight.cameraView.minY - cameraView.minY) < 1e-3
									&& Math.abs(inflight.cameraView.maxX - cameraView.maxX) < 1e-3
									&& Math.abs(inflight.cameraView.maxY - cameraView.maxY) < 1e-3;
								const sameFine = Math.abs(inflight.fineCellSize - fineCellSize) < 1e-9;
								if (sameView && sameFine) {
									// COALESCE: same-camera re-dispatch onto the in-flight solve.  Leave
									// `solveNeededRef.current` as-is (true from the latest camera move); the
									// in-flight solve's reply will re-prime when it settles.  No new dispatch.
									return;
								}
								// DISTINCT camera: fall through and dispatch a NEW solveTerrain for the latest camera. The worker-side
								// abort machinery cancels the prior in-flight solve — `refreshWorkingGridForCamera` aborts its
								// `AbortController` (still attached, prior solve hasn't settled) and bumps `pendingCameraToken`; the prior solve's per-V-cycle check throws `SolveAbortedError`.  We do NOT wait for the prior reply.
							} else if (solveSentRef.current) {
								// Defensive: `inFlightSolveCameraRef` was null but `solveSentRef` true — a
								// state inconsistency from an older code path.  Treat as coalesce (the safe
								// default that matches the pre-fix behaviour for this branch).
								return;
							}
							solveSentRef.current = true;
							solveNeededRef.current = true;  // stays true until a LATEST reply clears it
							const cameraGen = cameraGenRef.current;
							// Bump the monotonic attempt id and capture it for the reply-callback's staleness
							// check.  Stale prior-solve replies see their id below this and skip the state reset.
							const myAttemptId = ++solveAttemptIdRef.current;
							inFlightSolveCameraRef.current = {cameraView, fineCellSize};
							// Start the solve-phase timer (wall-clock dispatch→reply); the Stats panel
							// reads `lastSolveDispatchedAt` to show a live "in flight" duration.
							solveStartRef.current = performance.now();
							RunInAction_Set(()=>terrainProcessStats.lastSolveDispatchedAt = solveStartRef.current);
							wp.dispatchToWorker(
								0,
								{type: "solveTerrain", prepareds: collectPrepareds(), cameraView, fineCellSize, cameraGen, mode3D: perspectiveRef.current} as Record<string, unknown>,
								[],
								(data: Record<string, unknown>)=>{
									if (cancelled) return;
									// Staleness check: if a NEWER solve was dispatched during this solve's in-flight
									// window (rapid-zoom case), this prior reply is STALE — skip the state reset AND
									// the eval re-prime; the newer dispatch owns the state and re-primes when IT settles.
									if (solveAttemptIdRef.current !== myAttemptId) {
										// Stale reply.  Only the timer reset is safe (`solveStartRef` now belongs to the
										// latest dispatch).  Leave `solveNeededRef`/`solveSentRef`/`inFlightSolveCameraRef`
										// for the latest reply.
										if (solveStartRef.current != null) {
											// Only clear if this was the timer holder — a newer dispatch may have already overwritten
											// `solveStartRef` with its own start time; this branch only fires for the older attempt whose value
											// was already replaced.  Nothing to do here for the timer either (the latest dispatch owns it).
										}
										return;
									}
									// Latest reply: worker 0's solve + BroadcastChannel publish settled; receivers
									// have the fresh grid.
									solveNeededRef.current = false;
									solveSentRef.current = false;
									inFlightSolveCameraRef.current = null;
									// **Set15 P5 — surface the ACTUAL solved near-field cell size on this solve**
									// reply (the worker ships `WorkingGridResult.effectiveCellSize`; the real
									// resolution after the leaf-budget coarsening, not the requested `fineCellSize`).
									const solvedCellSize = (data as {solvedCellSize?: number}).solvedCellSize;
									if (solvedCellSize != null) {
										RunInAction_Set(()=>terrainProcessStats.fineCellSize_final = solvedCellSize);
									}
									if (solveStartRef.current != null) {
										RunInAction_Set(()=>{
											terrainProcessStats.lastSolveMs = performance.now() - solveStartRef.current!;
											terrainProcessStats.lastSolveDispatchedAt = null;
										});
										solveStartRef.current = null;
									}
									// Fresh solve settled — reset the eval-fill window so the first subsequent `evalChunkBatch` (in `dispatchBatchToWorker`) opens a new one.
									evalFillStartRef.current = null;
									// Materials-first (2026-09-01): the solve now has real heights.  Re-enqueue
									// any leaves that were flat-filled during the solve-pending window so the
									// true heights replace the flat fill (the eval re-prime below evaluates
									// them).  Clear the tracker first so a re-entrant solve can't double-count.
									const flatNodes = flatFilledForSolveRef.current;
									flatFilledForSolveRef.current = [];
									if (flatNodes.length > 0) qt.markNodesDirty(flatNodes);
									primeEvalAcrossAllWorkers();
								},
							);
						};

						/** Demand-pull helper (workerCount <= 1 fast path). */
						const dispatchNextEvalNode = (): void=>{
							if (cancelled) return;
							const nextNodes = qt.dequeueForWorker(evalChunkBatchSizeRef.current);
							if (nextNodes.length === 0) return;
							dispatchBatchToWorker(wp.roundRobinWorkerIdx(), nextNodes);
						};

						/** Finalise the eval-fill window when the eval queue is drained AND every dispatched batch has replied (in-flight count 0). Only ever fires from the eval reply callback — the empty-dequeue sites can never see completion (if the queue is empty but batches are in flight, the reply callback is the one that finalises; if nothing is in flight, `evalFillStartRef` is already null). Idempotent.
						 *
						 *  Set15.5 P6 (2026-08-19) — 3D HALT MODE: when the tree has hit the hard chunk ceiling
						 *  (`qt.evalHaltedAtCeiling`), the far-field leaves REMAIN in the eval queue forever
						 *  (that's the halt-vs-coarsen design — `dequeueForWorker` deliberately stops pulling
						 *  and never evicts/coarsens).  The remaining queued leaves are the intentionally-
						 *  unrendered far field, NOT work still in progress, so the fill is genuinely complete
						 *  once the in-flight batches have replied.  Without this, `qt.evalQueueLength > 0`
						 *  stays true forever under the halt → `checkEvalFillDrained` never finalises →
						 *  `lastEvalFillAt` never clears → the Stats panel's "Eval/Sample" label counts up
						 *  "in flight…" forever even though eval is done (`renderLimitHit` is the true signal
						 *  that the far field was intentionally left unevaluated). */
						const checkEvalFillDrained = (): void=>{
							if (evalFillStartRef.current == null) return;
							if (evalInFlightRef.current > 0) return;
							// Queue not drained: in 3D halt mode the residual queue is the intentionally-halted
							// far field (never drains — treat as complete); otherwise a non-empty queue means
							// real work remains → keep the window open.
							if (qt.evalQueueLength > 0 && !qt.evalHaltedAtCeiling) return;
							terrainProcessStats.lastEvalFillMs = performance.now() - evalFillStartRef.current;
							RunInAction_Set(()=>terrainProcessStats.lastEvalFillAt = null);
							evalFillStartRef.current = null;
						};

						/** Worker-local re-prime: dequeue a fresh batch and dispatch to the SAME worker.
						 *  Skips not-ready workers and those at/above `MAX_INFLIGHT_PER_WORKER` (share stays queued). */
						const redispatchSameWorker = (workerIdx: number): void=>{
							if (cancelled) return;
							if (solveNeededRef.current) return;  // no eval while solve pending
							if (!workerReadyForGen(workerIdx, cameraGenRef.current)) return;  // not ready yet
							if (workerAtInflightCap(workerIdx)) return;  // at/above in-flight cap — share stays queued
							const nextNodes = qt.dequeueForWorker(evalChunkBatchSizeRef.current);
							if (nextNodes.length === 0) return;
							dispatchBatchToWorker(workerIdx, nextNodes);
						};

						// Prime eval/solve. Only dispatch to workers ready for current camera gen
						// AND under MAX_INFLIGHT_PER_WORKER. Excluded workers' share stays queued.
						// Worker 0 ACKs; capped workers free up on reply; readiness listener re-primes.
						const primeEvalAcrossAllWorkers = (): void=>{
							if (cancelled) return;
							if (solveNeededRef.current) { dispatchSolveTerrain(); return; }
							const wc = wp.workerCount;
							if (wc <= 1) { dispatchNextEvalNode(); return; }
							const gen = cameraGenRef.current;
							// Count ELIGIBLE workers (ready AND under the in-flight cap); dequeue only their share so
							// not-ready OR capped workers' nodes stay queued.
							const batchSize = evalChunkBatchSizeRef.current;
							let eligibleCount = 0;
							for (let i = 0; i < wc; i++) {
								if (workerReadyForGen(i, gen) && !workerAtInflightCap(i)) eligibleCount++;
							}
							if (eligibleCount === 0) return;  // no worker eligible this tick (worker 0 ACKs once its solve settles; capped workers free up on reply)
							const budget = eligibleCount * batchSize;
							const allNodes = qt.dequeueForWorker(budget);
							if (allNodes.length === 0) return;
							const base = Math.floor(allNodes.length / eligibleCount);
							const remainder = allNodes.length % eligibleCount;
							let offset = 0;
							let slot = 0;
							for (let i = 0; i < wc; i++) {
								if (!workerReadyForGen(i, gen) || workerAtInflightCap(i)) continue;
								const sliceLen = base + (slot < remainder ? 1 : 0);
								if (sliceLen === 0) { slot++; continue; }
								const slice = allNodes.slice(offset, offset + sliceLen);
								offset += sliceLen;
								slot++;
								dispatchBatchToWorker(i, slice);
							}
						};

						// Set15.5 P6 — surface the live eval-pipeline state so the Stats panel can show
						// WHY the "Eval/Sample" window is still open: a non-empty eval queue (leaves still
						// queued) vs a stuck in-flight count (a dispatch awaiting a worker reply).  Written
						// every loop tick (cheap observable set) alongside the other Set15 P5 counters.
						RunInAction_Set(()=>{
							terrainProcessStats.evalQueueLength = qt.evalQueueLength;
							terrainProcessStats.evalInFlight = evalInFlightRef.current;
							terrainProcessStats.evalHaltedAtCeiling = qt.evalHaltedAtCeiling;
						});

						primeEvalAcrossAllWorkers();
					}

				// --- texture build ---
				// Set9 Phase 3 — the shader path renders per-chunk uniforms (u_geoNormal/u_baseAlbedo)
				// from the worker's heights + normals; the 33×33 albedo bake is retired under the flag,
				// so the textureQueue is drained (clearing dirty_texture) WITHOUT dispatching buildTexture.
				if (proceduralMaterialsRef.current) {
					for (const node of qt.dequeueForTextureWorker(4)) {
						if (node.chunk) qt.applyTextureResult(node);
					}
				} else {
					const texNodes = qt.dequeueForTextureWorker(4);
					for (const node of texNodes) {
						const {x, y, width: w, height: h} = node.bounds;
						const capturedVersion = node.chunk!.texVersion;
						const heightsCopy = new Float32Array(node.chunk!.heights);
						const normalsCopy = new Float32Array(node.chunk!.normals);
						const sc = shadingCfgRef.current;
						wp.dispatch(
							{
								type: "buildTexture",
								boundsX: x, boundsY: y, boundsW: w, boundsH: h,
								heights: heightsCopy, normals: normalsCopy,
								regions: terrainRegionsRef.current.map(r=>({
									tid: r.terrainID, rx: r.rect.x, ry: r.rect.y, rw: r.rect.width, rh: r.rect.height,
								})),
								activeTerrainIDs: [...activeTerrainIDsRef.current],
								colorStops: MobXTreeToPlainTree(colorGradRef.current.stops),
								shadingCfg: {light_azimuth: sc.light_azimuth, light_altitude: sc.light_altitude, ambient: sc.ambient, strength: sc.strength},
							} as Record<string, unknown>,
							[heightsCopy.buffer, normalsCopy.buffer],
							result=>{
								if (cancelled) return;
								const bitmap = result.bitmap as ImageBitmap;
								if (!node.chunk || node.chunk.dirty_data) { bitmap.close(); return; }
								if (node.chunk.texVersion !== capturedVersion) {
									bitmap.close();
									node.chunk.texInflight = false;
									return;
								}
								qt.applyTextureResult(node);
								const tex = bitmapToTexture(bitmap);
								texMapRef.current.get(node)?.dispose();
								texMapRef.current.set(node, tex);
								node.chunk.onEvict = ()=>{
									texMapRef.current.get(node)?.dispose();
									texMapRef.current.delete(node);
								};
								chunksDirtyRef.current = true;
							},
						);
					}
				}
			}
			setTimeout(loop, 0);
		};
		setTimeout(loop, 0);
		return ()=>{ cancelled = true; };
	}, []);

	// 250ms interval: trigger a render if new chunks were processed since last render
	// (reducing this interval can have smoother fill-ins, but also means more threejs render work)
	useEffect(()=>{
		const id = setInterval(()=>{
			if (chunksDirtyRef.current) {
				chunksDirtyRef.current = false;
				Invalidate3JS(invalidate, "terrain.interval", "chunks processed since last render");
			}
		}, 100);
		return ()=>clearInterval(id);
	}, [invalidate]);

	// Init quadtree, mesh pool and worker pool once
	useEffect(()=>{
		const qt = new TerrainQuadtree(new Rect(-ROOT_HALF, -ROOT_HALF, ROOT_HALF * 2, ROOT_HALF * 2, false));
		qtRef.current = qt;
		terrainQuadtree_current = qt;
		terrainChunkSplats_current = splatMapRef.current;
		poolRef.current = new ChunkMeshPool(scene);
		workerPoolRef.current = new TerrainWorkerPool(terrain.overlay_workerCount);
		terrainWorkerPool_current = workerPoolRef.current;
		// 2026-07-30 receiver-readiness ACK: seed all workers at -1; each climbs its slot
		// on its `receiverReady` ACK (worker 0's is posted from `refreshWorkingGridForCamera`
		// / `prepareTerrain`'s first solve), so it unblocks the gate identically to 1..N.
		{
			const wc = workerPoolRef.current.workerCount;
			readyGenByWorkerRef.current = Array.from({length: wc}, ()=>-1);
			inFlightByWorkerRef.current = Array.from({length: wc}, ()=>0);
		}
		// 2026-08-19 — this effect can re-run (pool teardown + recreate) when `scene`/`gl` change
		// identity (e.g. a `<Canvas key={mode}>` 2D↔3D remount).  The OLD pool's workers are
		// terminated, ORPHANING any `evalChunkBatch` dispatches still in flight: their reply
		// callbacks (which would have decremented `evalInFlightRef`) are dead.  If we don't reset
		// `evalInFlightRef` here, it stays at its stale non-zero value forever, so the Stats
		// "Eval/Sample" label never finalises its eval-fill window (queue is drained but
		// `evalInFlightRef > 0` → `checkEvalFillDrained` never fires → counts "in flight…" forever).
		// Mirror the `inFlightByWorkerRef` reset above.  Also clear the eval-fill window so the
		// fresh quadtree/prepared states start a clean one.
		evalInFlightRef.current = 0;
		evalFillStartRef.current = null;
		RunInAction_Set(()=>terrainProcessStats.lastEvalFillAt = null);
		workerPoolRef.current.setReceiverReadyListener((workerIdx, _cacheKey, cameraGen)=>{
			const arr = readyGenByWorkerRef.current;
			if (cameraGen > (arr[workerIdx] ?? -1)) arr[workerIdx] = cameraGen; else return;  // climb monotonic
			// Next `loop` tick re-primes now that this worker is ready; bump dirty to reschedule promptly.
			chunksDirtyRef.current = true;
		});
		// 2026-07-29 solve-share: worker 0 publishes solved-grid snapshots on a worker-side
		// BroadcastChannel to receivers (1..N), cutting the ~250 ms main-thread stop-the-world from
		// renderer-mediated fan-out and fixing the detached-ArrayBuffer crash on rapid zooms.
		// Set9 Phase 3 — lazily create the procedural-materials manager + assemble/compile the
		// dirt program when the shader path is first enabled (deferred until the toggle flips so a
		// non-shader session never pays the compile cost).  Errors fall back to the legacy bake.
		{
			const manager = new TerrainMaterialManager(map.id);
			materialManagerRef.current = manager;
			// 2026-08-25 — DEBUG SHIM: carry the PERSISTED `debugShimMats` setting into the
			// manager's shim mode BEFORE the first ensureProgram compiles anything.  The
			// setting-change block below only fires on a TOGGLE (shimPrevRef is seeded with the
			// initial value), so on a fresh page load with debugShimMats already persisted true
			// it would otherwise compile the REAL (expensive) shaders first — this init ensures
			// the first program is already the cheap single-compile shim.
			manager.__setDebugShim(terrain.debugShimMats);
			// `gl` from useThree is three's WebGLRenderer; the manager needs BOTH the raw GL2
			// context (module-cache compile) AND the WebGLRenderer (Phase 6 three.js-layer
			// program pre-warm — scratch ShaderMaterials compiled through the live renderer's
			// program cache so the first appearance is a cache HIT).
			manager.attachGL(gl.getContext() as WebGL2RenderingContext);
			manager.attachRenderer(gl);
			// The warm scratch renders must use the LIVE scene + camera (three folds light/shadow
			// counts + outputColorSpace into the program cacheKey) — see warmMaterialForProgram.
			manager.attachScene(scene, camera);
			// ...and the LIVE output config (toneMapping + outputColorSpace), which ToneMappingController
			// sets per-frame (not at renderer creation) — the warm program's cacheKey must match.
			manager.attachOutputConfig(()=>({
				toneMapping: terrain.toneMapping ?? 4,
				outputColorSpace: terrain.toneMapping == 0 ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace,
			}));
			// 2026-08-26 — re-ENABLED: a swallowed compile/link failure renders BLACK with zero
			// diagnostics (the deep-zoom black-chunk symptom), and the debug-shim already covers
			// the vscode/SwiftShader perf case the original disable targeted.
			// 2026-08-31 (Set13) — kept ON: three's onFirstUse readbacks (getProgramInfoLog etc.)
			// only block when a program is used BEFORE its compileAsync resolved. The manager now
			// shields still-compiling programs (TerrainMaterialProgram.compiling — getProgramForSet
			// returns null until the link lands), so nothing reaches setProgram->getUniforms->
			// onFirstUse until it's fully linked, making the readbacks cheap regardless.
			gl.debug.checkShaderErrors = true;
			const maybeInit = ()=>{
				if (!proceduralMaterialsRef.current || materialProgramRef.current) return;
				// Set9 Phase 5 — splat mode: the demo assignments' distinct recipe ids drive the
				// ≤4-way shader.  The recipe set is a per-frame-read of the (module-level)
				// assignments — the program recompiles when the set changes (D1 set-level cache).
				const recipeIDs = splatRecipeIDs(terrainBaseRecipeID(map.id));
				manager.ensureProgram(recipeIDs, {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true}).then(prog=>{
					if (materialManagerRef.current !== manager) { prog.material.dispose(); return; }
					materialProgramRef.current = prog;
					chunksDirtyRef.current = true;
				}).catch(err=>{
					console.error("[TerrainRenderer] procedural-materials program failed — falling back to the legacy bake path.", err);
				});
				// Set9 Phase 6 — program-cache warm-up: compile the OTHER reachable splat sets
				// ahead (idle-scheduled) so panning into a region whose chunk needs a different
				// eval-slot count never hitches.  Idempotent + deduped by set hash (D1/D10).
				scheduleMaterialWarmUp();
			};
			// Set9 Phase 6 — warm-up the material programs reachable from the CURRENT assignment
			// set, scheduled OFF the frame loop (idle time / a timeout chain).  Best-effort: a
			// compile error logs and is swallowed (the live ensureProgram surfaces it properly).
			// Also re-schedules whenever the assignment set changes (see the splat-recipe change
			// effect below) so a new recipe's programs are ready before the first chunk needs them.
			const scheduleMaterialWarmUp = ()=>{
				const m = materialManagerRef.current;
				if (!m) return;
				// Set12 P4 — the warm-up enumerates the DISTINCT per-chunk recipe sets from
				// region bbox ↔ chunk overlap (plan §4.3 A) — the sets the chunks actually
				// request — NOT the map-wide union's prefixes (those are gone).
				const sets = enumerateChunkRecipeSets(terrainMaterialRegions, chunkSizesForWarmUp(), {knownRecipeIDs: new Set(GetProcMatRecipes(map.id).map(a=>a.id)), baseRecipeID: terrainBaseRecipeID(map.id), maxMaterials: SPLAT_MAX_MATERIALS}).map(s=>s.recipeIDs);
				// Never block the frame loop: compile after the current task + any pending render
				// settle.  (requestIdleCallback would be ideal, but it's unavailable in workers
				// and spotty in some WebViews — a macrotask chain is portable + sufficient.)
				setTimeout(()=>{
					if (materialManagerRef.current !== m) return;
					m.warmUpPrograms(sets, {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true}).catch(err=>{
						console.warn("[TerrainRenderer] material warm-up failed (best-effort; the live program still compiles on demand).", err);
					});
				}, 0);
			};
			maybeInit();
			// Re-init the program when the shader-path toggle flips on.
			const disposeProgram = ()=>{
				if (materialProgramRef.current) {
					materialProgramRef.current.material.dispose();
					materialProgramRef.current = null;
				}
			};
			return ()=>{
				disposeProgram();
				manager.dispose();
				if (materialManagerRef.current === manager) materialManagerRef.current = null;
				for (const mat of chunkMaterialMapRef.current.values()) mat.dispose();
				chunkMaterialMapRef.current.clear();
				for (const splat of splatMapRef.current.values()) {
					splat.indexTex.dispose(); splat.weightTex.dispose(); splat.weightTex2.dispose();
					if (splat.regionTex) { splat.regionTex.dispose(); splat.paramsTex!.dispose(); }
				}
				splatMapRef.current.clear();
				qt.dispose();
				if (terrainQuadtree_current === qt) terrainQuadtree_current = null;
				if (terrainChunkSplats_current === splatMapRef.current) terrainChunkSplats_current = null;
				if (terrainWorkerPool_current === workerPoolRef.current) terrainWorkerPool_current = null;
				poolRef.current!.dispose();
				// Set15 P8 (§6.2) — dispose the SHARED shadow-depth material template (the
				// per-chunk clones are disposed in ChunkMeshPool.dispose()).
				if (shadowDepthMaterialRef.current) {
					shadowDepthMaterialRef.current.dispose();
					shadowDepthMaterialRef.current = null;
				}
				workerPoolRef.current!.terminate();
				for (const tex of texMapRef.current.values()) tex.dispose();
				texMapRef.current.clear();
			};
		}
	}, [scene, terrain.overlay_workerCount, gl]);

	// Re-dispatch `prepareTerrain` (clearing prepared states) when terrain mods, regions,
	// features, the constraint-cell overlay toggle, or Set6 GPU selection hash
	// changes. The overlay flag controls worker snapshot attachment to PreparedTerrainDataV3.
	// Set15 P2.5 (D3): `slope_scale` is RETIRED (pinned to 1.0), so it's intentionally absent from
	// the data key — a change to a persisted `slope_scale` no longer invalidates the worker data.
	const overlayEnabled = terrain.overlay_showConstraintCells;
	const gpuSelectionHash = JSON.stringify(gpu);
	// 2026-08-24 — the height-solver toggle joins the data key so a disable/enable clears the
	// prepared states (preparedStatesRef.clear() below) and the loop re-evaluates: disabling stops
	// dispatching `prepareTerrain`/`solveTerrain` + switches to flat-height eval; re-enabling
	// re-solves the worker grid.  `overlay_heightFallback` is intentionally NOT in the key (it only
	// affects flat-height mode, where the loop reads the ref every tick).
	const dataKey = JSON.stringify(terrainMods) + JSON.stringify(terrainRegions) + featuresHashRef.current + (overlayEnabled ? "1" : "0") + gpuSelectionHash + (terrain.overlay_heightSolverEnabled ? "1" : "0");
	const prevDataKeyRef = useRef<string>("");
	if (dataKey !== prevDataKeyRef.current) {
		prevDataKeyRef.current = dataKey;
		// Clear prepared states so prepareTerrain is re-dispatched for changed mods
		preparedStatesRef.current.clear();
		preparingRef.current.clear();
		for (const tex of texMapRef.current.values()) tex.dispose();
		texMapRef.current.clear();
		// Set9 Phase 5 — data changed: the chunk splats (pure functions of heights/normals) are
		// stale; drop them so the next frame re-rasterises from the fresh heights.
		for (const splat of splatMapRef.current.values()) {
			splat.indexTex.dispose(); splat.weightTex.dispose(); splat.weightTex2.dispose();
			if (splat.regionTex) { splat.regionTex.dispose(); splat.paramsTex!.dispose(); }
		}
		splatMapRef.current.clear();
		// 2026-07-31 feat-change permanent-black fix — reset the receiver-readiness gate on a feature/terrain-def
		// change too, mirroring the camera-move reset. Without this, a feature-only change (no camera move →
		// `cameraGenRef` does NOT bump) leaves `workerReadyForGen` true, so `primeEvalAcrossAllWorkers` dispatches eval to receivers 1..N BEFORE they've imported worker 0's new-cacheKey BroadcastChannel snapshot → `workerCaches.get(newCacheKey)` misses → zero-fill with `aborted=false` → permanent black tiles. Resetting to `-1` forces every worker to re-ACK `receiverReady` for the current gen before any eval dispatches.
		{
			const wc = workerPoolRef.current?.workerCount ?? 1;
			readyGenByWorkerRef.current = Array.from({length: wc}, ()=>-1);
		}
		// 2026-08-24 — re-enqueue every chunk so the next loop tick re-evaluates it under the new
		// height-solver mode: disabling re-runs them through the flat-height branch, re-enabling
		// through the worker eval once the fresh prepareTerrain lands (prepare's own reply also
		// calls markAllDirty, but in flat-height mode there is no prepare).  `markAllDirtyTexture`
		// alone wouldn't re-evaluate data — heights/normals must be recomputed too.
		qtRef.current?.markAllDirty();
	}

	// Mark all evaluated chunks dirty_texture when only light/color settings change
	const textureKey = JSON.stringify(resolvedColorGrad) + JSON.stringify({
		light_azimuth: resolvedShadingCfg.light_azimuth,
		light_altitude: resolvedShadingCfg.light_altitude,
		ambient: resolvedShadingCfg.ambient,
		strength: resolvedShadingCfg.strength,
	});
	const prevTextureKeyRef = useRef<string>("");
	if (textureKey !== prevTextureKeyRef.current) {
		prevTextureKeyRef.current = textureKey;
		// Only invalidate textures; normals/heights are still valid
		qtRef.current?.markAllDirtyTexture();
	}

	// Set9 Phase 3 — (re)assemble the material program when the shader-path toggle flips ON;
	// return to the legacy bake when it flips OFF.  2026-08-13 — on EITHER transition, mark
	// chunks texture-dirty (re-bake) + bump chunksDirtyRef (re-render); off keeps the program
	// cached (cheap re-enable — same program, D1).
	//
	// 2026-08-25 — DEBUG SHIM: the `debugShimMats` toggle flips the manager's shim mode
	// (swap the real material programs for a single trivial hashed-colour shim).  Detected here
	// alongside the procedural toggle so the re-assemble happens in the same place.
	// (Re)ensure the live fast-path program for the current set + shim mode.  Shared by the
	// procedural toggle and the debug-shim toggle below, so either change recompiles the
	// fast-path program the frame loop clones chunks from.
	const procMatPrevRef = useRef(terrain.proceduralMaterials);
	const shimPrevRef = useRef(terrain.debugShimMats);
	const ensureLiveProgram = ()=>{
		const manager = materialManagerRef.current;
		if (!manager || !proceduralMaterialsRef.current) return;
		manager.ensureProgram(splatRecipeIDs(terrainBaseRecipeID(map.id)), {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true}).then(prog=>{
			if (materialManagerRef.current !== manager) { prog.material.dispose(); return; }
			materialProgramRef.current = prog;
			chunksDirtyRef.current = true;
		}).catch(err=>{
			console.error("[TerrainRenderer] procedural-materials program failed — falling back to the legacy bake path.", err);
		});
	};
	const shimChanged = terrain.debugShimMats !== shimPrevRef.current;
	shimPrevRef.current = terrain.debugShimMats;
	if (shimChanged && terrain.proceduralMaterials) {
		const manager = materialManagerRef.current;
		if (manager) {
			// Swap the manager's shim mode (drops per-set programs; the fixed shim structural
			// hash means all sets map to the same trivial WebGLProgram — the perf escape hatch).
			manager.__setDebugShim(terrain.debugShimMats);
			// Re-ensure the fast-path program under the NEW mode (real shader when turning OFF,
			// or the single shim when turning ON).  The chunk clones are disposed so the frame
			// loop re-clones them from the freshly-ensured template.
			for (const mat of chunkMaterialMapRef.current.values()) mat.dispose();
			chunkMaterialMapRef.current.clear();
			materialProgramRef.current = null;
			ensureLiveProgram();
			chunksDirtyRef.current = true;
		}
	}
	if (terrain.proceduralMaterials !== procMatPrevRef.current) {
		if (terrain.proceduralMaterials) {
			if (!materialProgramRef.current) ensureLiveProgram();
			// Ensure a re-render even when the program is already cached (a re-enable right
			// after a disable) — the toggle alone doesn't schedule a frame under demand mode.
			chunksDirtyRef.current = true;
		} else {
			// Toggle OFF — return to the legacy bake.  Re-bake every evaluated chunk's 33×33
			// albedo bitmap (the shader path drained it without baking) + trigger a re-render.
			qtRef.current?.markAllDirtyTexture();
			chunksDirtyRef.current = true;
		}
	}
	procMatPrevRef.current = terrain.proceduralMaterials;

	useFrame(()=>{
		const qt = qtRef.current;
		const pool = poolRef.current;
		if (!qt || !pool || !terrain.overlay_enabled || terrainMods.length === 0) {
			// Hide all active meshes when disabled
			for (const m of activeMeshesRef.current) pool?.release(m);
			activeMeshesRef.current = [];
			return;
		}

		// Set15 P1 — the one camera seam (§3.5). Resolves BOTH camera types here so the ortho
		// path stays byte-identical (constant worldToScreen) and the perspective 3D camera renders.
		// Set15.5 P4 (§6.1/§6.2) — pass the quadtree's D-LESS height sampler so the perspective
		// metric becomes elevation-aware (`dz = eyeZ − terrainH`).  The sampler resolves to the
		// node's OWN chunk when present, else the nearest evaluated ANCESTOR (never a fresh 0 —
		// §6.2 bootstrap stability: a parent is always evaluated before it splits, so a freshly-
		// split child reads its ancestor's height rather than collapsing to eyeZ/0).  It is the
		// SAME lattice the 3D vertex shader displaces geometry by, so the metric measures the
		// point's ACTUAL rendered surface distance — the thing that was 64× under-estimated in §1.1.
		// The ORTHO branch ignores the sampler entirely (byte-identical 2D, §9).
		const viewInfo = resolveViewInfo(camera, size, settings.view3D.maxViewDistance,
			(x: number, y: number)=>qtRef.current?.sampleGeoHeightGeomAt_withAncestor(x, y) ?? null);
		// P2 — per-node metric.  The ortho path keeps the constant worldToScreen; the perspective
		// 3D camera evaluates it per quadtree node.  `nextFineCellSize` + `mpp` still want a single
		// representative number → use the viewport-centre evaluation.
		const worldToScreen = viewInfo.worldToScreenAt(
			viewInfo.cameraView.x + viewInfo.cameraView.width / 2,
			viewInfo.cameraView.y + viewInfo.cameraView.height / 2,
		);
		const isPerspective = viewInfo.isPerspective;
		// `cameraView` is the ZOOMED visible rect (viewInfo applies /zoom for ortho; the clamped
		// footprint for persp).
		const cvW = viewInfo.cameraView.width;
		const cvH = viewInfo.cameraView.height;

		// Viewport bounds expanded to 150% to pre-load just-off-screen chunks (ortho only — the
		// margin is meaningful because worldToScreen is constant).  Set15.5 P1 — persp now uses
		// the REAL ground-projected frustum footprint (`worldViewportBounds`, the plan §3.1
		// cull) instead of the old world-sized centred square; the margin is applied inside
		// `resolvePerspectiveViewportBounds` (§3.3).  `cameraView` stays the conservative square
		// (it feeds the SOLVE footprint + the representative worldToScreen sample).
		const MARGIN = 0.5; // 50% extra on each side = 200% total area
		const viewportBounds = isPerspective
			? (viewInfo.worldViewportBounds ?? viewInfo.cameraView)
			: new Rect(
				viewInfo.cameraView.x - cvW * (1 + MARGIN * 2) / 2,
				viewInfo.cameraView.y - cvH * (1 + MARGIN * 2) / 2,
				cvW * (1 + MARGIN * 2), cvH * (1 + MARGIN * 2), false,
			);

		// Two-grid (§7.1) camera view = the visible camera rectangle. The LRU margin
		// is for chunk pre-loading only; solver/rasterization padding lives in the
		// working grid, not here.
		// Set15 P3 — under PERSPECTIVE the `cameraView` is the whole horizon footprint (~131 km).
		// Solving that at a single `fineCellSize` blows the working-grid leaf budget, which coarsens
		// the cells to ~262 m and merges near-field detail ("one misshapen peak").  Cap the SOLVE
		// footprint to a radius around the SOLVE CENTRE (the eye + the viewpoint's
		// `detailedSolveCenter_distance` offset along its yaw direction); the far field is served by
		// the coarse macro grid / ring stack.  The radius/cell-size knobs live on the terrain def's
		// PoissonSolverConfig; the solve-centre offset lives on the active `Mod_Viewpoint`.
		const mode3D_workingGridRadius = isPerspective ? resolveMode3DWorkingGridRadius(resolvedPoissonCfg.mode3D_workingGridRadius) : 0;
		// The solve centre: the eye, offset toward the active viewpoint's horizontal look direction
		// by `detailedSolveCenter_distance` (metres).  Applies to BOTH solve routes (working grid +
		// ring stack) — both centre on `nextCameraView`.  In 2D there's no viewpoint offset (0).
		const solveCentreOffset = (()=>{
			if (!isPerspective) return {x: 0, y: 0};
			const vp = resolveActiveViewpoint(map.id, settings.view3D.view3D_activeViewpointObjID);
			const distance = vp?.mod.detailedSolveCenter_distance;
			if (!vp || distance == null || !Number.isFinite(distance) || distance <= 0) return {x: 0, y: 0};
			// Yaw convention (matches buildPerspectiveCamera): yaw measured from map +y, 0 = +y.
			// The modifier's yaw is local to the container; add the container's world rotation
			// (itself + ancestors) so a rotated container offsets the solve centre correctly.
			const yaw = vp.mod.yaw + (ResolveObjRotInWorld(vp.obj) ?? 0);
			const yawRad = yaw * Math.PI / 180;
			return {x: Math.sin(yawRad) * distance, y: Math.cos(yawRad) * distance};
		})();
		const nextCameraView = isPerspective
			? (()=>{
				const cx = viewInfo.eye.x + solveCentreOffset.x, cy = viewInfo.eye.y + solveCentreOffset.y;
				return {minX: cx - mode3D_workingGridRadius, minY: cy - mode3D_workingGridRadius, maxX: cx + mode3D_workingGridRadius, maxY: cy + mode3D_workingGridRadius};
			})()
			: {
				minX: viewInfo.cameraView.x,
				minY: viewInfo.cameraView.y,
				maxX: viewInfo.cameraView.x + cvW,
				maxY: viewInfo.cameraView.y + cvH,
			};
		// Fine cell size (SOLVE request): the requested working-grid/ring-0 cell size.
		// In 2D, sample every `maxPxPerSample`-th screen pixel, projected back to world metres
		// (lower = finer cells at a given zoom).  The setting feeding this is
		// `mode2D_solverFineCell_sizeAsPx` — it applies ONLY in 2D mode.
		// Set15 P3 — under PERSPECTIVE the solve cell size is pinned to the terrain-definition's
		// `mode3D_fineCellSize` (independent of eye distance, plan P5 ring-0 intent) so a high viewpoint
		// still resolves near-field detail.  The 2D screen-size knob has NO effect on the 3D solve:
		// the perspective camera's screen-projected size is not a meaningful solve-spacing input.
		// The eval/LOD path still uses `renderChunk_maxPxPerSample` for its chunk-split threshold
		// (separate knob, below).  The working-grid leaf budget can coarsen the result further.
		const nextFineCellSize = isPerspective
			? resolveMode3DFineCellSize(resolvedPoissonCfg.mode3D_fineCellSize)
			: (worldToScreen > 0 ? terrain.mode2D_solverFineCell_sizeAsPx / worldToScreen : 4);
		// Set15 P3 diagnostic — surface the live fine cell size + mode so the Stats panel can
		// compare the 2D vs 3D solver resolution (a large 3D value = terrain solved too coarse
		// under perspective).  Written unconditionally each frame (cheap observable set).
		RunInAction_Set(()=>{
			terrainProcessStats.fineCellSize_requested = nextFineCellSize;
			terrainProcessStats.isPerspective = isPerspective;
		});

		// sticky `cameraGenRef`: only bump when (cameraView, fineCellSize) changed
		// vs. last tick — a stable view must not lift the gen every frame.  Epsilon
		// mirrors the worker's `rectsClose` helper.
		const prev = lastCameraSnapRef.current;
		const sameView = Math.abs(prev.cameraView.minX - nextCameraView.minX) < 1e-3
			&& Math.abs(prev.cameraView.minY - nextCameraView.minY) < 1e-3
			&& Math.abs(prev.cameraView.maxX - nextCameraView.maxX) < 1e-3
			&& Math.abs(prev.cameraView.maxY - nextCameraView.maxY) < 1e-3;
		const sameFine = Math.abs(prev.fineCellSize - nextFineCellSize) < 1e-9;
		const cameraMoved = !(sameView && sameFine);
		if (cameraMoved) {
			cameraGenRef.current++;
			lastCameraSnapRef.current = {cameraView: nextCameraView, fineCellSize: nextFineCellSize};
			// seam fix: invalidate loaded chunks so they re-evaluate against the new
			// camera's working grid; otherwise carried-over data shows a viewport-boundary seam.
			// Mark before qt.update so split/merge coalesces with this invalidation.
			qt.markAllDirty();
			chunksDirtyRef.current = true;
			// Gate eval until worker 0 re-solves the grid for the new camera.  The gen bump above
			// also fails every existing ack (`cameraGenRef` is monotonic), so no explicit reset is needed.
			solveNeededRef.current = true;
			// 2026-08-03 rapid-zoom-queue fix — DO NOT clear `solveSentRef.current = false` here. The in-flight
			// marker is owned entirely by `dispatchSolveTerrain`'s reply callback (and `inFlightSolveCameraRef`);
			// clearing it on every camera move would WRONGLY let a second dispatch fire while a prior solve is still in flight for the same camera, doubling solves. The gate now detects a distinct-camera move by comparing `inFlightSolveCameraRef` against the latest camera signature and dispatches a new solve (aborting the prior in-flight one) — no `solveSentRef` reset needed.
			evalFillStartRef.current = null;
			RunInAction_Set(()=>terrainProcessStats.lastEvalFillAt = null);
		}
		cameraViewRef.current = nextCameraView;
		fineCellSizeRef.current = nextFineCellSize;
		// Set15 P5 — keep the solve-mode ref in sync so dispatch closures read the current mode.
		perspectiveRef.current = isPerspective;

		// Set15 P5 debug — surface the readiness gate + camera gen so the agent can see whether the
		// eval gate is blocking (a worker stuck below `cameraGen` never gets `evalChunkBatch`).
		RunInAction_Set(()=>{
			terrainProcessStats.workerReadyGens = [...readyGenByWorkerRef.current];
			terrainProcessStats.cameraGen = cameraGenRef.current;
		});

		// Step 1: update split/merge structure, culling nodes outside all terrain regions.
		// Set15.5 P3 (§5.1) — the P1-era `maxChunks` cap was DELETED: with P1's real frustum
		// footprint in `viewportBounds`, `update()`'s demand formula is correct for both camera
		// modes.  The P3 hard byte-derived ceiling (`MAX_CHUNKS_HARD_CEILING`) + graceful
		// coarsening (§5.3) replace it — no per-frame cap is threaded here anymore.
		// Set15.5 P2 — thread the lattice FLOOR (only for perspective; ortho omits `minLeafWidth` →
		// byte-identical 2D, §9).  `minSampleSpacing = solvedCell / setting`, `minLeafWidth = 32 ×
		// that` (CHUNK_N − 1 = 32 intervals across 33 samples, §4.1), where `solvedCell` is the
		// REALIZED solved cell size (fineCellSize_final — the value after any leaf-budget
		// coarsening) falling back to the REQUESTED `nextFineCellSize` before the first solve lands
		// (§4.5).  The setting is clamped [0.25, 64] via R1; a validated resolved floor is required.
		const renderFloor = isPerspective ? (()=>{
			// Set15.5 P2 + per-viewpoint override: the active viewpoint may set its OWN lattice-floor
			// value (for a viewpoint very close to the ground that needs finer resolution than the global
			// default).  `null`/absent on the viewpoint = fall back to the global MapSettings_Terrain value.
			// Both route through the same validated clamp (§8 R1), so a vanished/`undefined` value can never
			// silently disable the depth guard.
			const activeVP = resolveActiveViewpoint(map.id, settings.view3D.view3D_activeViewpointObjID);
			const vpFloor = activeVP?.mod.renderChunk_maxSamplesPerFineCellEdge;
			const setting = resolveRenderChunkMaxSamplesPerFineCellEdge(
				vpFloor != null ? vpFloor : terrain.renderChunk_maxSamplesPerFineCellEdge,
			);
			// §4.5 forward-compat — widen the floor to the finest L octave so newly-promoted L detail
			// (if maxWavelengthAsDetail is ever lowered) isn't silently clipped by a lattice that's
			// coarser than the real geometry.  `min(solvedCell, finestL)` keeps the floor as tight as
			// the field genuinely allows.
			const finestLWl = resolveFinestLWavelength(firstMod?.ambient);
			const solvedCell = terrainProcessStats.fineCellSize_final ?? nextFineCellSize;
			const minSampleSpacing = Math.min(solvedCell, finestLWl ?? solvedCell) / setting;
			return {minSampleSpacing, minLeafWidth: 32 * minSampleSpacing};
		})() : undefined;
		// Set15.5 P3 (§5.3) — thread the LOD-coarsening factor OUT/IN so `update()`'s hysteresis has
		// state across frames and so P5 can surface it (TerrainProcessStats.lodCoarseningFactor).
		// Set15.5 P6 (2026-08-19) — under PERSPECTIVE (3D), thread `haltAtCeiling` + `renderLimitHit`:
		// 3D halts at the hard ceiling (red warning) instead of coarsening.  The 3D camera is LOCKED
		// (no free pan/zoom), and P4's elevation-aware `worldToScreenAt` makes the single-centre
		// demand estimate unreliable (a peak/valley under the eye inflates it → spurious coarsening →
		// the "mountaintop goes blocky" bug).  Halt + warn is robust regardless of relief.  In 2D
		// these are omitted → the exact demand estimate + P3 coarsening path is unchanged (R2).
		// `renderLimitHit` is an observable-backed ref so the Stats panel warning can react to it.
		// Set15 P6 (§3.7) — NO `balance2to1` here.  The generalized edge stitch (per-edge level
		// delta + `2^k`-lerp, `edgeCoarserLevels`) handles ANY power-of-two level ratio, so the
		// 2:1-balance pass is NOT required.  Removing it eliminates the perpetual split/merge
		// re-enqueue it caused (the balance pass split a coarse neighbour that `updateNode` then
		// merged back next frame → the eval queue never drained → "Eval/Sample" never settled).
		qt.update({
			worldToScreenAt: viewInfo.worldToScreenAt,
			cameraCenter: {x: camera.position.x, y: camera.position.y},
			viewportBounds,
			minLeafWidth: renderFloor?.minLeafWidth,
			haltAtCeiling: isPerspective ? true : undefined,
			renderLimitHit: isPerspective ? {
				get: ()=>renderLimitHitRef.current,
				set: (v)=>RunInAction_Set(()=>renderLimitHitRef.current = v),
			} : undefined,
			lodCoarseningFactor: {
				get: ()=>lodCoarseningRef.current,
				set: (v)=>lodCoarseningRef.current = v,
			},
		}, terrainRegions, terrain.renderChunk_maxPxPerSample);

		// Set15.5 P5 (§7) — RENDER-LATTICE diagnostics.  The whole reason this set exists: every
		// SOLVE stat read healthy while the render lattice was 64× too coarse (§1.1).  These make
		// the RENDER side observable — `renderLatticeSpacing` directly comparable to
		// `fineCellSize_final`, so a large lattice↔solve ratio IS the bug, visible in the Stats panel.
		// `visibleLeafCount` makes P1's cull gain observable; `demandedLeafCount` vs `chunkCount`
		// surfaces P3's budget binding + saturation (the churn canary); `lodCoarseningFactor`
		// non-1.0 = the budget is binding and quality is being traded.
		{
			// Leaf width under the eye (the render lattice at the view centre).  `leafWidth/32`
			// = the realised sample spacing (CHUNK_N − 1 = 32 intervals across 33 samples, §4.1).
			const eyeWidth = qt.leafWidthAtPoint(viewInfo.eye.x, viewInfo.eye.y);
			RunInAction_Set(()=>{
				terrainProcessStats.renderLatticeSpacing_atViewCentre = eyeWidth != null ? eyeWidth / (CHUNK_N - 1) : null;
				terrainProcessStats.visibleLeafCount = qt.countVisibleLeaves(viewportBounds);
				terrainProcessStats.demandedLeafCount = qt.demandedLeafCount;
				terrainProcessStats.lodCoarseningFactor = lodCoarseningRef.current;
				terrainProcessStats.chunkCount = qt.chunkCount;
				terrainProcessStats.renderLimitHit = isPerspective ? renderLimitHitRef.current : null;
			});
		}

		// Step 2: release previously active meshes back to pool (chunk eval now runs in its own setTimeout loop)
		for (const m of activeMeshesRef.current) pool.release(m);
		activeMeshesRef.current = [];

		// Set15 P5 debug — push the live 3D render state so `window.lfTerrainDebug3D.refresh()`
		// can inspect the camera + mesh scene (agent debugging aid; no production cost).  Called
		// BEFORE the shader/legacy branches (both may `return` early).
		terrainDebug3DUpdate({
			camera,
			isPerspective,
			activeMeshCount: activeMeshesRef.current.length,
			poolSize: pool.size(),
			evaluatedChunks: (()=>{
				let n = 0;
				for (const leaf of qt.collectLeaves()) {
					const chunk = leaf.chunk;
					if (chunk && chunk.texVersion > 0) n++;
				}
				return n;
			})(),
			totalChunks: qt.collectLeaves().length,
			qt,
			scene,
			worldToScreen,
			cameraView: {x: viewInfo.cameraView.x, y: viewInfo.cameraView.y, width: viewInfo.cameraView.width, height: viewInfo.cameraView.height},
			eye: viewInfo.eye,
		});

		const texMap = texMapRef.current;
		const {overlay_opacity: opacity} = terrain;
		const shaderPath = proceduralMaterialsRef.current;
		const manager = materialManagerRef.current;
		const materialProg = materialProgramRef.current;

		// Set9 Phase 3 — shader path: per-chunk cloned ShaderMaterials with the worker's
		// geometric normal + the hypsometric-ramp base albedo as uniforms.  No bitmap, no
		// texMap — the bake is retired under this flag (plan §3, Phase 3 exit criteria).
		// Set9 Phase 5 — the splat flag rasterises each chunk's splat + uploads the DataTextures.
		if (shaderPath && manager && materialProg) {
			// Set12 P4 — when the REGION set changed, re-warm the reachable programs (idle-
			// scheduled; the warm-up is idempotent + deduped by set hash).  The warm-up now
			// enumerates the DISTINCT per-chunk recipe sets from region bbox ↔ chunk overlap
			// ({@link enumerateChunkRecipeSets} — plan §4.3 A): per-chunk sets are NOT prefixes
			// of one global list any more, so the old prefix enumeration would miss real sets.
			// Signature = the region layout + the base recipe (the materialRegionsSignature — the
			// enumerator's input), so a geometry/recipe/base edit re-schedules it.
			const warmSig = regionsSignature;
			if (warmSig != warmUpSignatureRef.current) {
				warmUpSignatureRef.current = warmSig;
				const m = materialManagerRef.current;
				if (m) {
					const sets = enumerateChunkRecipeSets(terrainMaterialRegions, chunkSizesForWarmUp(), {knownRecipeIDs: new Set(GetProcMatRecipes(map.id).map(a=>a.id)), baseRecipeID: terrainBaseRecipeID(map.id), maxMaterials: SPLAT_MAX_MATERIALS}).map(s=>s.recipeIDs);
					setTimeout(()=>{
						if (materialManagerRef.current !== m) return;
						m.warmUpPrograms(sets, {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true}).catch(err=>{
							console.warn("[TerrainRenderer] material warm-up failed (best-effort; the live program still compiles on demand).", err);
						});
					}, 0);
				}
			}
			// Per-frame shared values: meters-per-pixel + the light rig (constant per frame).
			const mpp = worldToScreen > 0 ? 1 / worldToScreen : 1;
			const {lightDir, ambient, strength} = lightRigRef.current;
			const slopeScale = slopeScaleRef.current;
			const splatEnabled = splatEnabledRef.current;
			// Set12 P2 — the splat is driven by the REGION set (module-level `terrainMaterialRegions`,
			// re-collected + published each render body) + the map's base recipe; the known-recipe
			// ids come from the catalogue (GetProcMatRecipes).  The old assignment set no longer
			// drives the rasterizer.
			const splatOpts = {regions: terrainMaterialRegions, baseRecipeID: terrainBaseRecipeID(map.id), knownRecipeIDs: GetProcMatRecipes(map.id).map(a=>a.id), maxMaterials: SPLAT_MAX_MATERIALS};
			// 2026-08-13 — the ACTIVE TerrainRegion AABBs (the quadtree's `terrainRegions`, filtered
			// to active terrain IDs like the legacy bake's `resolveTextureCtx`), forwarded to the
			// fragment shader so out-of-region fragments render at alpha 0 (restores the legacy bake
			// path's region clipping for the shader path).  Same world-AABB source as the region
			// loop above; identical to what the worker's legacy bake clips to.
			const terrainRegionAABBs = terrainRegionsRef.current
				.filter(r=>activeTerrainIDsRef.current.has(r.terrainID))
				.map(r=>({minX: r.rect.x, minY: r.rect.y, maxX: r.rect.x + r.rect.width, maxY: r.rect.y + r.rect.height}));

			// Set7 diagnostic — count chunks that fall back to variant row 0 (all-zeros → black).
			chunkVariantRow0CountRef.current = 0;

			for (const leaf of qt.collectLeaves()) {
				if (!leaf.chunk) continue;
				// 2026-08-08 issue-3 fix — render chunks that have EVER been evaluated (texVersion
				// >= 1), even when `dirty_data` (re-eval in flight).  Pre-fix: `if (dirty_data)
				// continue` blanked the whole terrain on every camera move (stage 1), then
				// never-evaluated zero-filled chunks (fresh Chunk() from dequeueForWorker, dirty
				// cleared optimistically) rendered as FLAT H + shader ∇D — the "pure noise, no
				// isoline heights" stage 2 — until the real reply landed (stage 3).  Keeping the
				// last-known data visible mirrors the legacy bake path's "has texMap" gate: the
				// terrain stays continuous during re-eval and swaps atomically on the fresh reply.
				// A texVersion-0 chunk has only zero-initialized arrays (never evaluated) → skip
				// (its first eval will populate it; until then it would render flat).
				if (leaf.chunk.texVersion === 0) continue;
				// Set12 P4 — the chunk's EXPECTED program (per-chunk recipe set, slot 0 = the
				// base): computed ONCE per frame from the same bbox-overlap + slot-assignment
				// logic the rasteriser's broad phase uses (chunkRecipeSetFor) and fetched from
				// the per-set program cache (warm-up enumerated exactly these sets via
				// enumerateChunkRecipeSets, so this is a cache HIT in the common case).  Falls
				// back to the fast-path program when the set was never warmed (cold start or an
				// enumerator miss).  Used both to create the chunk material AND to guard against
				// a stale clone (the region-signature block disposes + re-ensures programs on a
				// region edit — a chunk whose set changed identity must re-clone).
				const chunkSet = chunkRecipeSetFor(leaf.bounds.x, leaf.bounds.y, leaf.bounds.width, leaf.bounds.height, terrainMaterialRegions, splatOpts);
				const chunkSetProg = manager.getProgramForSet(chunkSet, {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true});
				const expectedProg = chunkSetProg ?? materialProg;
				// 2026-08-29 — OPTION C (SD3): whether this chunk's OWN per-chunk program exists.
				// When the warm-up is disabled (or a set was never enumerated), getProgramForSet
				// returns null and the chunk would settle on the MAP-WIDE fallback program — whose
				// eval-slot order (compiled over ALL regions) does NOT match this chunk's splat
				// slot order (compiled over its BBOX-FILTERED subset) → flipped/wrong materials
				// (the debug shim never shows this because it recolors per-chunk from the splat's
				// own recipeIDs).  A chunk without its per-chunk program is hidden until the
				// program self-heal (below) compiles it.  Under the debug shim every set shares the
				// one shim program, so this is always true there.
				const programReady = chunkSetProg != null || manager.isDebugShim;
				// 2026-08-29 — OPTION C (SD1/SD2): PROGRAM self-heal + (SD3) invisible-until-ready.
				// Correctness (slot order) requires this chunk's OWN per-chunk program to exist.
				// When the warm-up is disabled (or a set was never enumerated), getProgramForSet
				// returns null; the chunk MUST NOT render through the map-wide fallback, whose
				// eval-slot order (compiled over ALL regions) does not match this chunk's splat
				// slot order (compiled over its bbox-FILTERED subset) → flipped/wrong materials
				// (the debug shim never shows this because it recolors per-chunk from the splat's
				// own recipeIDs).  So we schedule the per-chunk ensure (deduped per set so the
				// same set across many chunks compiles once) and HOLD THE CHUNK HIDDEN until it
				// lands — SD3, never a wrong-material flash.  `chunkSet` here and the rasterized
				// `chunk.recipeIDs` are both `filterRegionsContributingToChunk` +
				// `assignRegionSlots` over the same region list, so they are order-identical by
				// construction — the rasterise block Asserts that equality so any future drift is
				// a loud failure instead of flipped materials.
				if (!programReady) {
					const healKey = chunkSet.join(",");
					if (!programHealPendingRef.current.has(healKey)) {
						programHealPendingRef.current.add(healKey);
						const m = manager;
						m.ensureProgram(chunkSet, {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true}).then(()=>{
							if (materialManagerRef.current !== m) return;
							programHealPendingRef.current.delete(healKey);
							chunksDirtyRef.current = true;
						}).catch(err=>{
							programHealPendingRef.current.delete(healKey);
							console.warn("[TerrainRenderer] per-chunk program self-heal ensure failed for set:", healKey, err);
						});
					}
					// SD3 — hold the chunk invisible until its per-chunk program is ready.
					continue;
				}
				// Set6→Set7 — the chunk's Layer-2 VARIANT (per recipe-identity-set param ROW).
				// Two identity sets sharing a structural hash (structurally-identical distinct
				// recipes — Grass3 vs Grass_Jungle) map to the SAME expectedProg but MUST bind
				// different ROWS, so we look up the variant by the chunk's identity set and set
				// its row on the clone.  A row swap is a plain uniform write (no re-clone).
				const chunkVariant = manager.getVariantForSet(chunkSet, {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true});
				const chunkVariantRow = chunkVariant?.row ?? 0;
				if (!chunkVariant) {
					chunkVariantRow0CountRef.current++;
					// 2026-08-26 — self-heal: a chunk whose identity set was never warmed (e.g. a
					// LOD size outside the warm-up sweep) would sit on variant row 0 (all-zeros
					// params → BLACK chunk) FOREVER — the warm-up signature hasn't changed, so
					// nothing re-runs for it.  Schedule an async ensureProgram for the chunk's
					// own set (idempotent: records the set's program + variant row) and dirty the
					// chunks once it lands, so the clone re-fetches the row next frame.
					// Deduped per set (a Set of memo keys) so a black region costs one ensure, not
					// one per chunk per frame.
					// 2026-08-28 — SHIM GUARD: under the debug shim, `recordVariant` is deliberately
					// SKIPPED (see TerrainMaterialManager.ensureProgram — the shim has no per-identity
					// param rows), so `getVariantForSet` can NEVER return non-null.  Scheduling the
					// self-heal there is a no-op that re-fires EVERY frame (each ensure resolves, marks
					// chunksDirtyRef, re-invalidates → the "terrain.interval keeps rendering" churn
					// loop).  Skip the heal entirely under shim mode — variant row 0 is the EXPECTED
					// (and only) state there, and the shim's per-slot colours make the region identity
					// visually distinct regardless of the row.
					const healKey = chunkSet.join(",");
					if (!manager.isDebugShim && !variantHealPendingRef.current.has(healKey)) {
						variantHealPendingRef.current.add(healKey);
						const m = manager;
						m.ensureProgram(chunkSet, {useSplat: true, useRegionParams: regionParamsEnabledRef.current, useVariantParams: true}).then(()=>{
							if (materialManagerRef.current !== m) return;
							variantHealPendingRef.current.delete(healKey);
							chunksDirtyRef.current = true;
						}).catch(err=>{
							variantHealPendingRef.current.delete(healKey);
							console.warn("[TerrainRenderer] variant self-heal ensure failed for set:", healKey, err);
						});
					}
				}
				// Guard 2 (§9.5) — acquire the variant row for THIS chunk (refcount it so eviction
				// won't reclaim it while the chunk is live).  On a variant-key change (the chunk's
				// identity set changed), release the old + acquire the new.  A per-frame no-op
				// when the key is unchanged.
				if (chunkVariant) {
					if (chunkVariantKeyMapRef.current.get(leaf) !== chunkVariant.key) {
						const prevKey = chunkVariantKeyMapRef.current.get(leaf);
						if (prevKey) manager.releaseVariantRow(prevKey);
						manager.acquireVariantRow(chunkVariant.key);
						chunkVariantKeyMapRef.current.set(leaf, chunkVariant.key);
					}
				} else if (chunkVariantKeyMapRef.current.has(leaf)) {
					// No variant (fallback row 0) — release any previously-acquired key.
					const prevKey = chunkVariantKeyMapRef.current.get(leaf) as string;
					manager.releaseVariantRow(prevKey);
					chunkVariantKeyMapRef.current.delete(leaf);
				}
				let mat = chunkMaterialMapRef.current.get(leaf);
				if (!mat) {
					// Clone from the chunk's own program's template (per-set UBO); chunks whose
					// set is unknown fall back to the fast-path program.
					mat = expectedProg === materialProg ? manager.cloneChunkMaterial() : manager.cloneChunkMaterialForProgram(expectedProg);
					(mat as unknown as {programSetHash?: string}).programSetHash = expectedProg.setHash;
					chunkMaterialMapRef.current.set(leaf, mat);
					leaf.chunk.onEvict = ()=>{
						// Guard 2 (§9.5) — release the chunk's variant row AFTER disposing the
						// material (the material holds the u_variantRow value being reclaimed).
						const vkey = chunkVariantKeyMapRef.current.get(leaf);
						if (vkey) { manager.releaseVariantRow(vkey); chunkVariantKeyMapRef.current.delete(leaf); }
						chunkMaterialMapRef.current.get(leaf)?.dispose();
						chunkMaterialMapRef.current.delete(leaf);
						const splat = splatMapRef.current.get(leaf);
						if (splat) {
							splat.indexTex.dispose(); splat.weightTex.dispose(); splat.weightTex2.dispose();
							if (splat.regionTex) { splat.regionTex.dispose(); splat.paramsTex!.dispose(); }
							splatMapRef.current.delete(leaf);
						}
						const geoN = geoNormalMapRef.current.get(leaf);
						if (geoN) { geoN.tex.dispose(); geoNormalMapRef.current.delete(leaf); }
						const geoH = geoHeightMapRef.current.get(leaf);
						if (geoH) { geoH.tex.dispose(); geoHeightMapRef.current.delete(leaf); }
						const geoHGeom = geoHeightGeomMapRef.current.get(leaf);
						if (geoHGeom) { geoHGeom.tex.dispose(); geoHeightGeomMapRef.current.delete(leaf); }
						const awm = ambientWeightMapRef.current.get(leaf);
						if (awm) { awm.tex.dispose(); ambientWeightMapRef.current.delete(leaf); }
					};
				}
				// Set12 P4 — re-clone guard: the chunk's expected program may differ from the
				// program its material was cloned from (a region edit re-ensured the fast-path
				// program, or a set whose identity changed).  Compare set hashes (a cheap id
				// comparison — the program carries its setHash); a mismatch drops the old clone
				// + re-clones from the fresh program.  Set7 — the VARIANT is NOT part of this
				// guard: a row swap is a plain uniform write (setChunkVariantRow below), so a
				// param-value change never re-clones (the old Set6 variantKey re-clone is dropped).
				if ((mat as unknown as {programSetHash?: string}).programSetHash !== expectedProg.setHash) {
					mat.dispose();
					mat = expectedProg === materialProg ? manager.cloneChunkMaterial() : manager.cloneChunkMaterialForProgram(expectedProg);
					(mat as unknown as {programSetHash?: string}).programSetHash = expectedProg.setHash;
					chunkMaterialMapRef.current.set(leaf, mat);
				}
				const heights = leaf.chunk.heights;
				const normals = leaf.chunk.normals;
				// Set12 P2 — rasterise this chunk's splat (cached per chunk, keyed by texVersion —
				// the texture queue's "data recomputed" signal).  The rasterizer now emits per-slot
				// SIGNED DISTANCES from the region set (plan §3.3); the packer thresholds at 0
				// (weight = d < 0 ? 1 : 0) so P2 renders byte-identical to the Set9 hard weights.
				// A rasterise error (data typo) logs once + the chunk falls back to slot 0 (dirt)
				// — never black tiles.
				if (splatEnabled && materialProg.setHash.includes("splat")) {
					let splat = splatMapRef.current.get(leaf);
					const texVersion = leaf.chunk.texVersion;
					if (!splat || splat.texVersion != texVersion) {
						try {
							const raster = rasterizeSplatFromArrays(
								leaf.bounds.x, leaf.bounds.y, leaf.bounds.width, leaf.bounds.height,
								heights, normals, CHUNK_N, splatOpts, splatOpts.regions, splatOpts.baseRecipeID,
							);
							// 2026-08-29 — OPTION C (SD2): the chunk's splat slot order (its OWN
							// `raster.chunk.recipeIDs`, computed by `assignRegionSlots` over the
							// rasterizer's narrow-phase-filtered subset) MUST equal the derived
							// `chunkSet` this chunk's program was keyed/compiled by — both come
							// from `filterRegionsContributingToChunk` + `assignRegionSlots` over
							// the same region list.  If they ever diverge, the program's eval-slot
							// order would mismatch the splat's slot order → flipped materials
							// WITHOUT any obvious error.  Assert here so a future refactor that
							// breaks the invariant fails loudly instead of silently misrendering.
							Assert(raster.chunk.recipeIDs.length == chunkSet.length && raster.chunk.recipeIDs.every((id, i)=>id == chunkSet[i]),
								`per-chunk program slot order (${chunkSet.join(",")}) != rasterized splat slot order (${raster.chunk.recipeIDs.join(",")}) — the per-chunk program would misread the splat's slots`);
							// 2026-08-29 — Set8 (SD4): the chunk's slot order MUST also agree with
							// the slot order the per-chunk PROGRAM was compiled in.  `chunkSetProg`
							// is fetched by `getProgramForSet` → `setMemoKey`, which is now
							// ORDER-SENSITIVE, so a correct program is keyed by THIS chunk's exact
							// order.  But if the program was actually compiled from a DIFFERENT
							// order with the same member set (a dominance inversion that changes
							// only the eval-slot order, and whose recipes happen to be
							// structurally-identical so the set-hash sequence also collides), the
							// variant row is still packed in THIS chunk's order and the eval code
							// is interchangeable — so a slot-order mismatch here is benign.  It is
							// a bug ONLY when it collides with structurally-DIFFERENT recipes
							// (eval_i's code would then misread the splat's slots).  Assert that
							// case fails loudly: when this chunk's program carries a different
							// order AND the member set has two structurally-different recipes at
							// the flipped positions, sharing the program is a silent misrender.
							if (chunkSetProg && chunkSetProg.recipeIDs.length == chunkSet.length && chunkSetProg.recipeIDs.some((id, i)=>id != chunkSet[i])) {
								// Two orders for the same member set — legal only if the permuted
								// recipes are structurally identical (eval code is interchangeable).
								const progOrderHash = chunkSetProg.recipeIDs.map(id=>ResolveRecipe(id, map.id).structuralHash);
								const chunkOrderHash = chunkSet.map(id=>ResolveRecipe(id, map.id).structuralHash);
								// The two orders share the same member set ⇒ same multiset of
								// hashes; the flip positions are where the SEQUENCES differ.
								for (let i = 0; i < chunkSetProg.recipeIDs.length; i++) {
									if (chunkSetProg.recipeIDs[i] != chunkSet[i] && progOrderHash[i] !== chunkOrderHash[i]) {
										throw new Error(`per-chunk program slot order (${chunkSetProg.recipeIDs.join(",")}) != chunk splat slot order (${chunkSet.join(",")}) with structurally-DIFFERENT recipes at the flip — sharing one program would misread the splat's slots (Set8; permutation-uniform fallback is the fix for this case)`);
									}
								}
							}
							// Set12 P4 — the over-stack DIAGNOSTIC (plan §4.3 C): the rasterizer
							// reports the regions whose recipes exceeded the per-chunk material cap
							// (dropped per chunk — the data still renders).  Surface it once per
							// chunk so authors see WHERE they over-stacked (the P4 test suite
							// treats "the diagnostic fires where expected" as first-class).
							if (raster.overflowDropped.length > 0) {
								console.warn(`[TerrainRenderer] material over-stack at chunk (${leaf.bounds.x.toFixed(0)}, ${leaf.bounds.y.toFixed(0)}, ${leaf.bounds.width.toFixed(0)}m): ${raster.overflowDropped.join(", ")} dropped — a chunk renders ≤${SPLAT_MAX_MATERIALS} materials (Set12 §4.3 C).`);
							}
							const texs = buildSplatTextures(raster.chunk);
							// Replace the previous splat textures (data changed).
							if (splat) { splat.indexTex.dispose(); splat.weightTex.dispose(); splat.weightTex2.dispose(); if (splat.regionTex) { splat.regionTex.dispose(); splat.paramsTex!.dispose(); } }
							// 2026-08-26 — DEBUG SHIM: keep the chunk's OWN slot order (raster.chunk.recipeIDs)
							// on the cached entry — setChunkSplatDist uses it to rewrite the per-chunk
							// u_debugShimColors palette (the shared shim program's slot indices are
							// per-chunk; see TerrainMaterialManager.setChunkSplatDist).
							splat = {...texs, texVersion, slotDominance: raster.chunk.slotDominance, slotFeather: raster.chunk.slotFeather, slotSoftUnion: raster.chunk.slotSoftUnion, slotNoiseAmp: raster.chunk.slotNoiseAmp, slotNoiseScale: raster.chunk.slotNoiseScale, chunkRecipeIDs: raster.chunk.recipeIDs};
							// Set12 P2 — per-region params texture (plan §6): the region payload
							// (winning region per sample) + the params texture (one row per region).
							// Only when the program was assembled with the region-params path.
							if (materialProg.setHash.includes("regionParams") && raster.chunk.regions != null) {
								const regionTexAndParams = buildRegionTexturesForMap(raster.chunk.regions, splatOpts.regions, map.id);
								if (regionTexAndParams) {
									splat.regionTex = regionTexAndParams.regionTex;
									splat.paramsTex = regionTexAndParams.paramsTex;
								}
							}
							splatMapRef.current.set(leaf, splat);
						} catch (err) {
							// SplatRasterizeError: a data problem (unknown recipe / >4 materials).
							// The chunk still renders — the fallback (slot 0 = dirt) fills it.
							if (err instanceof SplatRasterizeError) console.error("[TerrainRenderer] splat rasterise failed:", err.message);
							else throw err;
							splat = undefined;
						}
					}
					if (splat) {
						manager.setChunkSplatDist(mat, splat.indexTex, splat.weightTex, splat.weightTex2, splat.slotDominance, splat.slotFeather, splat.slotSoftUnion, splat.slotNoiseAmp, splat.slotNoiseScale, splat.chunkRecipeIDs);
						if (splat.regionTex && splat.paramsTex) {
							manager.setChunkRegionParams(mat, splat.regionTex, splat.paramsTex);
						}
						manager.setChunkSize(mat, leaf.bounds.width);
					}
				}
				// Set7 — set the chunk's VARIANT row (per recipe-identity param values; row 0 =
				// the fallback when no variant is known).  A plain uniform write each frame (cheap;
				// binds the shared texture + sets u_variantRow).  This is what makes Grass3 and
				// Grass_Jungle (structurally identical, same program) render their OWN values.
				manager.setChunkVariantRow(mat, chunkVariantRow);
				// Base albedo = the hypsometric ramp at the chunk's centre elevation (plan §8: the
				// ramp is DEMOTED to a base albedo input; centre sample is a cheap Phase 3 approx).
				const centreIdx = Math.floor(CHUNK_N / 2) * CHUNK_N + Math.floor(CHUNK_N / 2);
				const centreElev = heights[centreIdx] ?? 0;
				const albedo = sampleHypsometricRampJS(centreElev, colorGradRef.current.stops);
				// Per-sample geometric normal LATTICE (u_geoNormal): the worker's full 33×33
				// normals (slope_scale already applied, z-up) — the SAME per-sample normals the
				// old CPU bake applied per texel.  Cached per chunk keyed by texVersion (rebuilt
				// only when heights/normals change).  The shader's matGeoNormal() fetches this per
				// fragment at the fragment's lattice coord, giving hillshade + mask-slope FINE-CELL
				// granularity — the Phase-3 centre-sample uniform collapsed both to one slope per
				// chunk (hillshade "squares" ~12× too wide).
				const geoNEntry = geoNormalMapRef.current.get(leaf);
				const texVersion = leaf.chunk.texVersion;
				if (!geoNEntry || geoNEntry.texVersion != texVersion) {
					if (geoNEntry) geoNEntry.tex.dispose();
					const tex = buildGeoNormalTexture(normals);
					geoNormalMapRef.current.set(leaf, {tex, texVersion});
					manager.setChunkGeoNormal(mat, tex);
				} else {
					manager.setChunkGeoNormal(mat, geoNEntry.tex);
				}
				// 2026-08-13 — per-chunk HEIGHT lattice (u_geoHeight): the worker's full 33×33
				// heights (metres), the `geoHeight` primitive's elevation-reactive colour input.
				// Cached per chunk keyed by texVersion like u_geoNormal.  The shader's
				// matGeoHeight() fetches this per fragment at the fragment's lattice coord.
				const geoHEntry = geoHeightMapRef.current.get(leaf);
				if (!geoHEntry || geoHEntry.texVersion != texVersion) {
					if (geoHEntry) geoHEntry.tex.dispose();
					const tex = buildGeoHeightTexture(heights);
					geoHeightMapRef.current.set(leaf, {tex, texVersion});
					manager.setChunkGeoHeight(mat, tex);
				} else {
					manager.setChunkGeoHeight(mat, geoHEntry.tex);
				}
				// Set15 P3 (§3.4) — per-chunk D-LESS HEIGHT lattice (u_geoHeightGeom): the worker's
				// `geoHeightGeom` field (the D-excluded elevation — the geometry-displacement
				// input).  Bound for EVERY chunk (even 2D) so the `u_displaceScale`-gated vertex
				// fetch always reads valid data; the shape is identical to u_geoHeight (R32F 33×33),
				// so buildGeoHeightTexture is reused.  Cached by texVersion like u_geoHeight.
				const geoHGeomEntry = geoHeightGeomMapRef.current.get(leaf);
				if (!geoHGeomEntry || geoHGeomEntry.texVersion != texVersion) {
					if (geoHGeomEntry) geoHGeomEntry.tex.dispose();
					const tex = buildGeoHeightTexture(leaf.chunk.geoHeightGeom);
					geoHeightGeomMapRef.current.set(leaf, {tex, texVersion});
					manager.setChunkGeoHeightGeom(mat, tex);
				} else {
					manager.setChunkGeoHeightGeom(mat, geoHGeomEntry.tex);
				}
				// Issue 2 (2026-08-08) — per-chunk ambient-weight mask (R32F, CHUNK_N×CHUNK_N):
				// the worker's multiplier closure lattice (null = no multiplier ring).  Cached by
				// texVersion like u_geoNormal; the shader multiplies ∇D by it.  No mask → flag 0.
				const mask = leaf.chunk.ambientWeightMask;
				const awmEntry = ambientWeightMapRef.current.get(leaf);
				if (mask) {
					if (!awmEntry || awmEntry.texVersion != texVersion) {
						if (awmEntry) awmEntry.tex.dispose();
						const tex = buildAmbientWeightMaskTexture(mask);
						ambientWeightMapRef.current.set(leaf, {tex, texVersion});
						manager.setChunkAmbientWeight(mat, tex);
					} else {
						manager.setChunkAmbientWeight(mat, awmEntry.tex);
					}
				} else {
					if (awmEntry) { awmEntry.tex.dispose(); ambientWeightMapRef.current.delete(leaf); }
					manager.setChunkAmbientWeight(mat, null);
				}
				// Per-frame values are set on EVERY chunk material each frame (clones copy the
				// template's uniforms only once at clone time, so they can't inherit updates).
				// Set15 P3 — the 3D-mode values: `u_displaceScale` 1 (displace geometry), the
				// region-clip discard (D5), the real view vector (D10), and an OPAQUE material
				// (depthWrite, opacity 1) are all 3D-only; 2D passes displaceScale 0 + the
				// transparent overlay opacity → byte-identical.
				const effectiveOpacity = isPerspective ? 1 : opacity;
				const displaceScale = isPerspective ? 1 : 0;
				manager.setFrameUniforms(mat, mpp, lightDir, ambient, strength, slopeScale, effectiveOpacity, displaceScale, [camera.position.x, camera.position.y, camera.position.z]);
				// Set15 P3 (D5) — the 3D material variant: OPAQUE (blending off → alpha ignored,
				// depthWrite matters), DoubleSide (the far-side skirt walls face away), and
				// depthWrite on.  Set once per material clone (cheap); 2D clones stay transparent.
				mat.transparent = !isPerspective;
				mat.depthWrite = isPerspective;
				mat.side = isPerspective ? THREE.DoubleSide : THREE.FrontSide;
				// Set10 — the ambient-detail (sub-lattice band) fractal parameters (per-config,
				// set each frame with the light rig so a config change reaches every chunk's clone).
				manager.setAmbientDetailUniforms(mat, ambientDetailValuesRef.current);
				manager.setChunkOrigin(mat, leaf.bounds.x, leaf.bounds.y);
				manager.setChunkSize(mat, leaf.bounds.width);
				// Set15 P6 (§3.7) — the generalized edge stitch.  Per-chunk per-edge COARSENESS LEVELS
				// from the quadtree (the level delta per edge; 0 = same/finer).  The stitch works for
				// ANY power-of-two level ratio (the shader lerps between the two nearest coarse samples,
				// which are this chunk's own lattice samples at `2^k`-spaced indices), so NO 2:1 balance
				// pass is needed — the tree is not forced to split/merge to maintain balance, which
				// eliminates the perpetual split/merge re-enqueue ("Eval/Sample never settles").  Only
				// meaningful under perspective — in 2D the `u_edgeStitchEnabled` gate is 0 so these are
				// ignored, keeping the pre-P6 skirt behavior byte-identical.  Set every frame so a LOD
				// change re-stitches promptly.
				if (isPerspective) manager.setChunkEdgeCoarser(mat, qt.edgeCoarserLevels(leaf));
				// 2026-08-13 — apply the terrain-region clip (the shader zeroes alpha outside the
				// active TerrainRegion AABBs, so a coarse LOD chunk that only partially overlaps a
				// region doesn't render opaque fill far beyond its bounds).
				manager.setChunkTerrainRegions(mat, terrainRegionAABBs);
				manager.setChunkUniforms(mat, albedo);
				// Set15 P8 (§6.2) — the per-chunk SHADOW uniforms (reads the module-global shadow
				// light; 2D → enabled 0 → getShadowMask returns 1.0, byte-identical).
				manager.setShadowUniforms(mat, {
					light: terrainShadowLight_current,
					mapSize: terrainShadowMapSize_current,
				});
				// Set15 P3 (§3.3/§3.7) — the only per-mode GEOMETRY branch (D4): 2D uses the flat
				// 2-triangle quad; 3D uses the shared subdivided+skirt geometry (displaced in the
				// vertex shader).  z = 0 in 3D so the displaced elevation IS the world z.
				const mesh = isPerspective ? pool.acquireShaderMaterial3D(leaf.bounds, mat) : pool.acquireShaderMaterial(leaf.bounds, mat);
				// Set15 P8 (§6.2) — shadow participation: the 3D terrain RECEIVES (its fragment
				// samples the shadow map via u_shadow* uniforms) and CASTS (via a custom depth
				// material that reproduces the displacement — see below).  The 2D path is flat
				// quads + no shadow light → neither flag is meaningful; leave them false so a
				// chunk reused from the pool doesn't carry stale flags across a mode switch.
				mesh.receiveShadow = isPerspective && terrainShadowLight_current != null;
				mesh.castShadow = isPerspective && terrainShadowLight_current != null;
				if (isPerspective && terrainShadowLight_current != null) {
					// The casting depth material reproduces the vertex displacement. Clone the
					// SHARED template per chunk (per-chunk u_geoHeightGeom differs); the GL program
					// is shared (three caches on the onBeforeCompile-produced shader source +
					// customProgramCacheKey).
					if (!shadowDepthMaterialRef.current) {
						shadowDepthMaterialRef.current = createTerrainDepthMaterial();
					}
					if (!mesh.customDepthMaterial) {
						mesh.customDepthMaterial = shadowDepthMaterialRef.current.clone();
					}
					const geoHGeomEntry = geoHeightGeomMapRef.current.get(leaf);
					if (geoHGeomEntry?.tex) {
						setTerrainDepthUniforms(mesh.customDepthMaterial as THREE.ShaderMaterial, geoHGeomEntry.tex, 1);
					}
				}
				activeMeshesRef.current.push(mesh);
			}
			return;
		}

		for (const leaf of qt.collectLeaves()) {
			// Don't skip `dirty_data` leaves; a chunk with a prior `texMap` entry keeps rendering its old texture until its
			// fresh reply atomically swaps in a new one (the reply callbacks do dispose+set).
			if (!leaf.chunk) continue;
			const tex = texMap.get(leaf);
			if (!tex) continue;

			const mesh = pool.acquire(leaf.bounds, tex, opacity, isPerspective);
			activeMeshesRef.current.push(mesh);
		}
	});

	return null;
});

// ============================================================
// V3 helpers (§7.1 two-grid protocol)
// ============================================================

/** World bbox covering the union of all raster features' footprints, with a small
 *  margin (matches the test-helper `expandFeaturesBBox`). When there are no
 *  features, falls back to the renderer's root quad (±ROOT_HALF). */
function worldBBoxForFeatures(features: RasterFeature[]): {minX: number; minY: number; maxX: number; maxY: number} {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	const absorb = (x: number, y: number)=>{
		if (x < minX) minX = x; if (y < minY) minY = y;
		if (x > maxX) maxX = x; if (y > maxY) maxY = y;
	};
	for (const f of features) {
		switch (f.kind) {
			case "closedRing": for (const p of f.polyline) absorb(p.x, p.y); break;
			case "openCurve": for (const p of f.polyline) absorb(p.x, p.y); break;
			case "cliff": for (const p of f.polyline) absorb(p.x, p.y); break;
			case "spotHeight": absorb(f.x, f.y); break;
		}
	}
	if (!isFinite(minX)) return {minX: -ROOT_HALF, minY: -ROOT_HALF, maxX: ROOT_HALF, maxY: ROOT_HALF};
	const w = maxX - minX, h = maxY - minY;
	const side = Math.max(w, h, 1) * 1.10; // 10% margin per §3.4
	const cx = (minX + maxX) * 0.5, cy = (minY + maxY) * 0.5;
	return {minX: cx - side * 0.5, minY: cy - side * 0.5, maxX: cx + side * 0.5, maxY: cy + side * 0.5};
}

// Folds tessellated coords so any shape-affecting field re-triggers a solve.
// See rasterFeatureSignature in ConstraintRasterizer.ts for the regen root cause.
function featuresSliceSignature(features: RasterFeature[]): string {
	return rasterFeaturesSignature(features);
}